// src/components/companies/Companies.jsx
// ─────────────────────────────────────────────────────────────────────────────
//  TelcoFair · Companies
//  "TelcoFair Glass Workspace" — same system as Dashboard.jsx / Users.jsx.
//  Layout is pure CSS Grid; the certificate sheet is deliberately opaque and
//  filter-free so html2canvas can rasterise it faithfully.
// ─────────────────────────────────────────────────────────────────────────────

import React, {
    useState,
    useEffect,
    useRef,
    useMemo,
    useCallback,
} from 'react';
import {
    Box,
    Container,
    Paper,
    Typography,
    TextField,
    Button,
    Switch,
    FormControlLabel,
    Avatar,
    IconButton,
    Chip,
    InputAdornment,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    Tooltip,
    Dialog,
    DialogContent,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Skeleton,
    Tabs,
    Tab,
    CircularProgress,
    Fade,
} from '@mui/material';
import {
    Search as SearchIcon,
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Block as BlockIcon,
    CheckCircle as CheckCircleIcon,
    Refresh as RefreshIcon,
    Business as BusinessIcon,
    VerifiedUser as VerifiedUserIcon,
    TrendingUp as TrendingUpIcon,
    TrendingDown as TrendingDownIcon,
    LocationOn as LocationIcon,
    Language as LanguageIcon,
    Save as SaveIcon,
    Visibility as VisibilityIcon,
    Verified as VerifiedIcon,
    FiberManualRecord as LiveIcon,
    Email as EmailIcon,
    Phone as PhoneIcon,
    Storefront as StorefrontIcon,
    Close as CloseIcon,
    Payment as PaymentIcon,
    Receipt as ReceiptIcon,
    Check as CheckIcon,
    Download as DownloadIcon,
    Subscriptions as SubscriptionsIcon,
    WorkspacePremium as PackageIcon,
    EmojiEvents as EmojiEventsIcon,
    FilterAltOff as FilterOffIcon,
    Groups as GroupsIcon,
    CalendarToday as CalendarIcon,
    NotesOutlined as NotesIcon,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { db } from '../../config/firebase';
import PlatformLogo from '../common/PlatformLogo';
import {
    collection,
    getDocs,
    getDoc,
    doc,
    updateDoc,
    deleteDoc,
    setDoc,
    serverTimestamp,
} from 'firebase/firestore';
import toast from 'react-hot-toast';
import { safeExternalUrl } from '../../utils/safeNavigation';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

/* ═══════════════════════════════════════════════════════════════════════════
   1 · DESIGN TOKENS  (identical to Dashboard.jsx / Users.jsx — extract to
       src/theme/liquid.jsx once this has rolled across the app)
   ═══════════════════════════════════════════════════════════════════════════ */

const palette = {
    canvas: '#F2F7F9',
    canvasDeep: '#E7F0F4',
    paper: '#FFFFFF',
    ink: '#182235',
    inkSoft: '#506175',
    inkLight: '#7B8B9C',
    inkFaint: '#AAB6C1',
    hairline: 'rgba(31, 62, 82, 0.09)',
    hairlineSoft: 'rgba(31, 62, 82, 0.055)',

    rust: 'var(--primary, #2E6F8E)',
    rustDeep: 'var(--secondary, #42B89B)',
    rustGlow: 'var(--primary, #2E6F8E)',

    amber: '#D78B28',
    amberGlow: '#F1B75F',

    pine: '#2E8B69',
    pineGlow: '#69B99A',

    teal: '#268AA3',
    tealGlow: '#64B8C9',

    plum: '#8D5AC8',
    plumGlow: '#B78BDF',

    indigo: '#596BCE',
    indigoGlow: '#8796E2',

    success: '#1C8A56',
    warning: '#D18A0E',
    danger: '#C9342B',
};

const ACCENTS = [palette.rust, palette.teal, palette.amber, palette.pine, palette.plum, palette.indigo];

const fontDisplay = "'Plus Jakarta Sans', 'Inter', sans-serif";
const fontBody = "'Plus Jakarta Sans', 'Inter', sans-serif";
const fontMono = "'JetBrains Mono', 'SFMono-Regular', monospace";

const FONT_IMPORT = `@import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&family=JetBrains+Mono:wght@400;500;600;700&display=swap');`;

const hexToRgb = (hex) => {
    if (typeof hex !== 'string') return { r: 22, g: 18, b: 16 };
    let h = hex.replace('#', '').trim();
    if (h.length === 3) h = h.split('').map((c) => c + c).join('');
    if (h.length !== 6 || /[^0-9a-f]/i.test(h)) return { r: 22, g: 18, b: 16 };
    return {
        r: parseInt(h.slice(0, 2), 16),
        g: parseInt(h.slice(2, 4), 16),
        b: parseInt(h.slice(4, 6), 16),
    };
};

const rgba = (color, a = 1) => {
    if (typeof color === 'string' && color.startsWith('var(')) return `color-mix(in srgb, ${color} ${Math.max(0, Math.min(100, Number(a) * 100))}%, transparent)`;
    if (typeof color === 'string' && color.startsWith('rgb')) return color;
    const { r, g, b } = hexToRgb(color);
    return `rgba(${r}, ${g}, ${b}, ${a})`;
};

const liquid = (accent = palette.rust, { depth = 1, radius = 26 } = {}) => ({
    position: 'relative',
    borderRadius: `${radius}px`,
    background: `linear-gradient(155deg, ${rgba('#FFFFFF', 0.82)} 0%, ${rgba('#FFFFFF', 0.55)} 42%, ${rgba(accent, 0.07)} 100%)`,
    backdropFilter: 'blur(22px) saturate(180%)',
    WebkitBackdropFilter: 'blur(22px) saturate(180%)',
    border: `1px solid ${rgba('#FFFFFF', 0.6)}`,
    boxShadow: [
        `inset 0 1px 0 ${rgba('#FFFFFF', 0.95)}`,
        `inset 0 -1px 0 ${rgba('#FFFFFF', 0.35)}`,
        `inset 0 0 0 1px ${rgba(accent, 0.06)}`,
        `0 ${2 * depth}px ${6 * depth}px ${rgba(palette.ink, 0.04)}`,
        `0 ${10 * depth}px ${30 * depth}px -${8 * depth}px ${rgba(accent, 0.16)}`,
    ].join(', '),
    overflow: 'hidden',
    transition: 'box-shadow .5s cubic-bezier(.22,1,.36,1), transform .5s cubic-bezier(.22,1,.36,1), border-color .4s ease',
});

const useSheen = () => {
    const ref = useRef(null);
    const onPointerMove = useCallback((e) => {
        const el = ref.current;
        if (!el) return;
        const r = el.getBoundingClientRect();
        el.style.setProperty('--mx', `${((e.clientX - r.left) / r.width) * 100}%`);
        el.style.setProperty('--my', `${((e.clientY - r.top) / r.height) * 100}%`);
        el.style.setProperty('--sheen', '1');
    }, []);
    const onPointerLeave = useCallback(() => {
        const el = ref.current;
        if (el) el.style.setProperty('--sheen', '0');
    }, []);
    return { ref, onPointerMove, onPointerLeave };
};

const sheenLayer = (accent) => ({
    content: '""',
    position: 'absolute',
    inset: 0,
    pointerEvents: 'none',
    opacity: 'var(--sheen, 0)',
    transition: 'opacity .45s ease',
    background: `radial-gradient(420px circle at var(--mx, 50%) var(--my, 0%), ${rgba('#FFFFFF', 0.75)}, ${rgba(accent, 0.10)} 38%, transparent 68%)`,
    mixBlendMode: 'soft-light',
});

const glassField = (accent = palette.rust) => ({
    '& .MuiOutlinedInput-root': {
        borderRadius: '14px',
        fontSize: '0.875rem',
        background: rgba('#FFFFFF', 0.6),
        backdropFilter: 'blur(8px)',
        transition: 'box-shadow .3s ease',
        '& fieldset': { borderColor: palette.hairline, borderWidth: '1.5px' },
        '&:hover fieldset': { borderColor: rgba(accent, 0.4) },
        '&.Mui-focused fieldset': { borderColor: accent, borderWidth: '1.5px' },
        '&.Mui-focused': { boxShadow: `0 0 0 4px ${rgba(accent, 0.12)}` },
    },
    '& .MuiInputLabel-root.Mui-focused': { color: accent },
});

/* ═══════════════════════════════════════════════════════════════════════════
   2 · HELPERS
   ═══════════════════════════════════════════════════════════════════════════ */

const toDate = (value) => {
    if (!value) return null;
    if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
    if (typeof value?.toDate === 'function') {
        try { return value.toDate(); } catch { return null; }
    }
    if (typeof value === 'number') return new Date(value);
    if (typeof value === 'string') {
        const d = new Date(value);
        return Number.isNaN(d.getTime()) ? null : d;
    }
    return null;
};

const formatDate = (value) => {
    const d = toDate(value);
    if (!d) return '—';
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const compact = (n) => {
    const v = Number(n) || 0;
    if (Math.abs(v) >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
    if (Math.abs(v) >= 10_000) return `${(v / 1000).toFixed(1)}K`;
    return Math.round(v).toLocaleString();
};

const CURRENCY_SYMBOLS = { EUR: '€', GBP: '£', USD: '$', INR: '₹', AED: 'د.إ' };
const currencySymbol = (code) => CURRENCY_SYMBOLS[String(code || '').toUpperCase()] || '$';

const CYCLE_SUFFIX = {
    monthly: '/mo',
    annual: '/yr',
    yearly: '/yr',
    quarterly: '/qtr',
};

const priceLabel = (subscription) => {
    if (!subscription) return '—';
    const amount = Number(subscription.price) || 0;
    return `${currencySymbol(subscription.currency || 'EUR')}${amount.toLocaleString()}${CYCLE_SUFFIX[String(subscription.billingCycle || '').toLowerCase()] || ''}`;
};

const COMPANY_TYPES = [
    { value: 'operator', label: 'Operator', accent: palette.rust },
    { value: 'vendor', label: 'Vendor', accent: palette.plum },
    { value: 'aggregator', label: 'Aggregator', accent: palette.teal },
    { value: 'solution_provider', label: 'Solution Provider', accent: palette.pine },
    { value: 'consultant', label: 'Consultant', accent: palette.amber },
    { value: 'mvno', label: 'MVNO', accent: palette.indigo },
    { value: 'reseller', label: 'Reseller', accent: palette.tealGlow },
];

const typeMeta = (value) =>
    COMPANY_TYPES.find((t) => t.value === value) || {
        value: value || 'other',
        label: value ? String(value).replace(/_/g, ' ') : 'Unspecified',
        accent: palette.inkLight,
    };

const REGIONS = ['North America', 'Latin America', 'Europe', 'Asia Pacific', 'Middle East', 'Africa'];

const COMPANY_SIZES = ['1-10', '11-50', '51-200', '201-500', '501-1000', '1000+'];

const lifecycleOf = (c) => {
    if (c?.isBlocked) return 'blocked';
    if (c?.isActive === false) return 'inactive';
    if (!c?.isVerified) return 'pending';
    return 'active';
};

const LIFECYCLE_META = {
    active: { label: 'Active', tone: palette.success },
    pending: { label: 'Pending', tone: palette.warning },
    inactive: { label: 'Inactive', tone: palette.inkLight },
    blocked: { label: 'Blocked', tone: palette.danger },
};

const PAYMENT_META = {
    not_required: { label: 'Not required', tone: palette.inkLight },
    paid: { label: 'Paid', tone: palette.success },
    approved: { label: 'Approved', tone: palette.success },
    pending: { label: 'Pending', tone: palette.warning },
    review: { label: 'Under review', tone: palette.indigo },
    rejected: { label: 'Rejected', tone: palette.danger },
    manual: { label: 'Manual', tone: palette.plum },
};

const SUBSCRIPTION_META = {
    active: { label: 'Active', tone: palette.success },
    pending_payment: { label: 'Payment pending', tone: palette.warning },
    trial: { label: 'Trial', tone: palette.indigo },
    one_time: { label: 'One-time', tone: palette.amber },
    expired: { label: 'Expired', tone: palette.danger },
    cancelled: { label: 'Cancelled', tone: palette.inkLight },
    inactive: { label: 'None', tone: palette.inkLight },
};

const URL_RE = /^https?:\/\/[^\s.]+\.[^\s]{2,}$/i;

const normaliseUrl = (value) => {
    const v = String(value || '').trim();
    if (!v) return '';
    const candidate = /^https?:\/\//i.test(v) ? v : `https://${v}`;
    return safeExternalUrl(candidate, { allowBlob: false });
};

/* A registration number must never change between what the operator sees on
   screen and what lands in the PDF — so it is DERIVED from the company id
   (stable, offline, collision-resistant enough for a display code) and, once a
   certificate is downloaded, persisted back to the document. */
const derivedRegistrationNumber = (company) => {
    if (company?.registrationNumber) return company.registrationNumber;
    const seed = String(company?.id || company?.companyId || company?.companyName || 'telcofair');
    let h1 = 0x811c9dc5;
    let h2 = 0x01000193;
    for (let i = 0; i < seed.length; i += 1) {
        h1 = ((h1 ^ seed.charCodeAt(i)) * 16777619) >>> 0;
        h2 = ((h2 + seed.charCodeAt(i) * (i + 7)) * 2654435761) >>> 0;
    }
    const prefix = (company?.companyName || 'TFC').replace(/[^a-z]/gi, '').slice(0, 3).toUpperCase().padEnd(3, 'X');
    const block1 = h1.toString(36).toUpperCase().slice(0, 4).padStart(4, '0');
    const block2 = h2.toString(36).toUpperCase().slice(0, 6).padStart(6, '0');
    return `${prefix}-${block1}-${block2}`;
};

const useCountUp = (target, duration = 900) => {
    const [display, setDisplay] = useState(0);
    const fromRef = useRef(0);

    useEffect(() => {
        const to = Number(target) || 0;
        const from = fromRef.current;
        if (from === to) { setDisplay(to); return undefined; }

        let raf;
        const start = performance.now();
        const tick = (now) => {
            const p = Math.min((now - start) / duration, 1);
            const eased = 1 - Math.pow(1 - p, 3);
            const current = from + (to - from) * eased;
            fromRef.current = current;
            setDisplay(current);
            if (p < 1) raf = requestAnimationFrame(tick);
            else fromRef.current = to;
        };
        raf = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(raf);
    }, [target, duration]);

    return display;
};

/* ═══════════════════════════════════════════════════════════════════════════
   3 · LIQUID BACKDROP
   ═══════════════════════════════════════════════════════════════════════════ */

const LiquidBackdrop = React.memo(function LiquidBackdrop() {
    const motes = useMemo(
        () =>
            Array.from({ length: 16 }, (_, i) => ({
                id: i,
                size: 3 + ((i * 5) % 8),
                x: (i * 41) % 100,
                y: (i * 59) % 100,
                duration: 28 + ((i * 4) % 16),
                delay: (i * 1.9) % 12,
                color: ACCENTS[i % ACCENTS.length],
            })),
        []
    );

    return (
        <Box sx={{ position: 'fixed', inset: 0, zIndex: 0, overflow: 'hidden', pointerEvents: 'none' }}>
            <Box component="svg" sx={{ position: 'absolute', width: 0, height: 0 }} aria-hidden>
                <defs>
                    <filter id="tf-co-liquid">
                        <feTurbulence type="fractalNoise" baseFrequency="0.009 0.013" numOctaves="2" seed="11" result="noise">
                            <animate attributeName="baseFrequency" dur="32s" values="0.009 0.013;0.015 0.007;0.009 0.013" repeatCount="indefinite" />
                        </feTurbulence>
                        <feDisplacementMap in="SourceGraphic" in2="noise" scale="66" xChannelSelector="R" yChannelSelector="G" />
                    </filter>
                </defs>
            </Box>

            <motion.div
                animate={{ x: [0, -60, 40, 0], y: [0, 40, -30, 0], scale: [1, 1.12, 0.92, 1] }}
                transition={{ duration: 31, repeat: Infinity, ease: 'easeInOut' }}
                style={{
                    position: 'absolute', top: '-8%', left: '-5%',
                    width: 600, height: 600, borderRadius: '50%',
                    background: `radial-gradient(circle at 40% 40%, ${rgba(palette.rustGlow, 0.3)}, ${rgba(palette.rust, 0.12)} 46%, transparent 72%)`,
                    filter: 'url(#tf-co-liquid) blur(44px)',
                }}
            />
            <motion.div
                animate={{ x: [0, 60, -35, 0], y: [0, -40, 30, 0], scale: [1, 0.9, 1.14, 1] }}
                transition={{ duration: 37, repeat: Infinity, ease: 'easeInOut' }}
                style={{
                    position: 'absolute', bottom: '-12%', right: '-6%',
                    width: 560, height: 560, borderRadius: '50%',
                    background: `radial-gradient(circle at 55% 45%, ${rgba(palette.plumGlow, 0.22)}, ${rgba(palette.teal, 0.12)} 48%, transparent 72%)`,
                    filter: 'url(#tf-co-liquid) blur(50px)',
                }}
            />
            <motion.div
                animate={{ x: ['-50%', '-56%', '-45%', '-50%'], y: ['-50%', '-45%', '-56%', '-50%'], scale: [1, 1.07, 0.95, 1] }}
                transition={{ duration: 27, repeat: Infinity, ease: 'easeInOut' }}
                style={{
                    position: 'absolute', top: '42%', left: '52%',
                    width: 720, height: 720, borderRadius: '50%',
                    background: `radial-gradient(circle, ${rgba(palette.amberGlow, 0.15)}, transparent 66%)`,
                    filter: 'blur(80px)',
                }}
            />

            {motes.map((m) => (
                <motion.span
                    key={m.id}
                    initial={{ opacity: 0 }}
                    animate={{
                        y: [`${m.y}%`, `${m.y - 40}%`, `${m.y}%`],
                        x: [`${m.x}%`, `${m.x + 12}%`, `${m.x}%`],
                        opacity: [0, 0.45, 0],
                        scale: [0.4, 1, 0.4],
                    }}
                    transition={{ duration: m.duration, delay: m.delay, repeat: Infinity, ease: 'easeInOut' }}
                    style={{
                        position: 'absolute', top: 0, left: 0,
                        width: m.size, height: m.size, borderRadius: '50%',
                        background: `radial-gradient(circle at 30% 30%, #fff, ${m.color})`,
                        boxShadow: `0 0 12px ${rgba(m.color, 0.5)}`,
                    }}
                />
            ))}

            <Box
                sx={{
                    position: 'absolute', inset: 0, opacity: 0.5,
                    backgroundImage: `linear-gradient(${rgba(palette.ink, 0.022)} 1px, transparent 1px),
                                      linear-gradient(90deg, ${rgba(palette.ink, 0.022)} 1px, transparent 1px)`,
                    backgroundSize: '72px 72px',
                    maskImage: 'radial-gradient(circle at 50% 25%, #000 0%, transparent 78%)',
                    WebkitMaskImage: 'radial-gradient(circle at 50% 25%, #000 0%, transparent 78%)',
                }}
            />
        </Box>
    );
});

/* ═══════════════════════════════════════════════════════════════════════════
   4 · PRIMITIVES
   ═══════════════════════════════════════════════════════════════════════════ */

const Rule = ({ color = palette.rust, w = 44 }) => (
    <Box
        sx={{
            width: w, height: 3, mt: 0.75, borderRadius: 3,
            background: `linear-gradient(90deg, ${color}, ${rgba(color, 0)})`,
            animation: 'tfRule 3.4s ease-in-out infinite',
        }}
    />
);

const GlassIcon = ({ icon, color, size = 42 }) => (
    <Box
        sx={{
            width: size, height: size, flexShrink: 0,
            borderRadius: `${size * 0.34}px`,
            display: 'grid', placeItems: 'center',
            color: '#fff',
            background: `linear-gradient(150deg, ${rgba(color, 0.95)}, ${rgba(color, 0.72)})`,
            boxShadow: `inset 0 1px 0 ${rgba('#FFFFFF', 0.55)}, inset 0 -2px 6px ${rgba(palette.ink, 0.18)}, 0 8px 20px -6px ${rgba(color, 0.55)}`,
            '& svg': { fontSize: size * 0.48, filter: 'drop-shadow(0 1px 1px rgba(0,0,0,.18))' },
        }}
    >
        {icon}
    </Box>
);

const GlassPanel = ({
    accent = palette.rust,
    delay = 0,
    lift = false,
    padding = { xs: 2.25, md: 2.75 },
    children,
    sx = {},
    ...rest
}) => {
    const { ref, onPointerMove, onPointerLeave } = useSheen();
    return (
        <motion.div
            initial={{ opacity: 0, y: 22, filter: 'blur(6px)' }}
            animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
            transition={{ duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }}
            style={{ height: '100%', width: '100%', minWidth: 0 }}
        >
            <Paper
                ref={ref}
                onPointerMove={onPointerMove}
                onPointerLeave={onPointerLeave}
                elevation={0}
                sx={{
                    ...liquid(accent),
                    p: padding,
                    height: '100%',
                    minWidth: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    boxSizing: 'border-box',
                    '&::after': sheenLayer(accent),
                    ...(lift && {
                        '&:hover': {
                            transform: 'translateY(-5px)',
                            boxShadow: `inset 0 1px 0 ${rgba('#FFFFFF', 1)}, 0 26px 56px -18px ${rgba(accent, 0.34)}`,
                        },
                    }),
                    ...sx,
                }}
                {...rest}
            >
                <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 }}>
                    {children}
                </Box>
            </Paper>
        </motion.div>
    );
};

const PanelHead = ({ icon, title, subtitle, accent = palette.rust, chip, right, dense = false }) => (
    <Box
        sx={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            gap: 1.5, flexWrap: 'wrap', mb: dense ? 1.75 : 2.5,
        }}
    >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
            {icon && <GlassIcon icon={icon} color={accent} size={dense ? 36 : 42} />}
            <Box sx={{ minWidth: 0 }}>
                <Typography
                    sx={{
                        fontFamily: fontDisplay, fontWeight: 600,
                        fontSize: dense ? '1.02rem' : '1.14rem',
                        color: palette.ink, letterSpacing: '-0.015em', lineHeight: 1.15,
                    }}
                >
                    {title}
                </Typography>
                {subtitle ? (
                    <Typography sx={{ fontSize: '0.76rem', color: palette.inkLight, mt: 0.25 }}>{subtitle}</Typography>
                ) : (
                    <Rule color={accent} w={dense ? 34 : 44} />
                )}
            </Box>
            {chip && (
                <Chip
                    label={chip}
                    size="small"
                    sx={{
                        height: 24, fontFamily: fontMono, fontSize: '0.62rem', fontWeight: 600,
                        letterSpacing: '0.04em', textTransform: 'uppercase',
                        color: accent, bgcolor: rgba(accent, 0.09),
                        border: `1px solid ${rgba(accent, 0.16)}`,
                    }}
                />
            )}
        </Box>
        {right}
    </Box>
);

const EmptyState = ({ icon, title, text, action, onAction }) => (
    <Box sx={{ textAlign: 'center', py: 6, px: 2 }}>
        <Box sx={{ display: 'grid', placeItems: 'center', mb: 1.5 }}>
            <GlassIcon icon={icon} color={palette.inkFaint} size={54} />
        </Box>
        {title && (
            <Typography sx={{ fontFamily: fontDisplay, fontWeight: 600, fontSize: '1.05rem', color: palette.ink }}>
                {title}
            </Typography>
        )}
        <Typography sx={{ color: palette.inkLight, fontSize: '0.85rem', mt: 0.5 }}>{text}</Typography>
        {action && (
            <Button
                startIcon={<AddIcon />}
                onClick={onAction}
                sx={{
                    mt: 2.25, textTransform: 'none', fontWeight: 700, borderRadius: '14px', px: 2.5, py: 1,
                    color: '#fff',
                    background: `linear-gradient(140deg, ${palette.rust}, ${palette.rustDeep})`,
                    boxShadow: `inset 0 1px 0 ${rgba('#FFFFFF', 0.3)}, 0 14px 30px -14px ${rgba(palette.rust, 1)}`,
                    '&:hover': { background: `linear-gradient(140deg, ${palette.rustGlow}, ${palette.rust})` },
                }}
            >
                {action}
            </Button>
        )}
    </Box>
);

const StatCard = ({ label, value, icon, accent, delta, hint, delay = 0, loading }) => {
    const animated = useCountUp(value);
    const { ref, onPointerMove, onPointerLeave } = useSheen();
    const trendColor =
        delta?.trend === 'up' ? palette.success : delta?.trend === 'down' ? palette.danger : palette.inkLight;

    return (
        <motion.div
            initial={{ opacity: 0, y: 24, filter: 'blur(8px)' }}
            animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
            transition={{ duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }}
            style={{ height: '100%', minWidth: 0 }}
        >
            <Paper
                ref={ref}
                onPointerMove={onPointerMove}
                onPointerLeave={onPointerLeave}
                elevation={0}
                sx={{
                    ...liquid(accent, { radius: 24 }),
                    height: '100%', p: { xs: 2, md: 2.4 },
                    display: 'flex', flexDirection: 'column', minWidth: 0,
                    '&::after': sheenLayer(accent),
                    '&:hover': {
                        transform: 'translateY(-7px) scale(1.012)',
                        boxShadow: `inset 0 1px 0 ${rgba('#FFFFFF', 1)}, 0 28px 60px -20px ${rgba(accent, 0.42)}`,
                    },
                    '&:hover .tf-orb': { opacity: 0.4, transform: 'scale(1.25)' },
                }}
            >
                <Box
                    className="tf-orb"
                    sx={{
                        position: 'absolute', top: -70, right: -70, width: 170, height: 170,
                        borderRadius: '50%', filter: 'blur(38px)', opacity: 0.16,
                        background: `radial-gradient(circle, ${accent}, transparent 68%)`,
                        transition: 'opacity .6s ease, transform .6s cubic-bezier(.22,1,.36,1)',
                        pointerEvents: 'none',
                    }}
                />
                <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', height: '100%' }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, mb: 1.75 }}>
                        <GlassIcon icon={icon} color={accent} size={40} />
                        {delta && (
                            <Chip
                                size="small"
                                icon={delta.trend === 'down' ? <TrendingDownIcon sx={{ fontSize: 13 }} /> : <TrendingUpIcon sx={{ fontSize: 13 }} />}
                                label={delta.label}
                                sx={{
                                    height: 22, fontFamily: fontMono, fontSize: '0.6rem', fontWeight: 700,
                                    color: trendColor, bgcolor: rgba(trendColor, 0.09),
                                    border: `1px solid ${rgba(trendColor, 0.18)}`,
                                    '& .MuiChip-icon': { color: trendColor, ml: '6px' },
                                }}
                            />
                        )}
                    </Box>

                    {loading ? (
                        <Skeleton width="60%" height={40} sx={{ bgcolor: palette.hairline }} />
                    ) : (
                        <Typography
                            sx={{
                                fontFamily: fontDisplay, fontWeight: 600,
                                fontSize: { xs: '1.85rem', md: '2.2rem' },
                                lineHeight: 1, letterSpacing: '-0.03em',
                                background: `linear-gradient(140deg, ${palette.ink} 20%, ${accent})`,
                                WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
                                fontVariantNumeric: 'tabular-nums',
                            }}
                        >
                            {compact(animated)}
                        </Typography>
                    )}

                    <Typography
                        sx={{
                            mt: 0.9, fontFamily: fontMono, fontSize: '0.62rem', fontWeight: 500,
                            textTransform: 'uppercase', letterSpacing: '0.12em', color: palette.inkSoft,
                        }}
                    >
                        {label}
                    </Typography>
                    <Typography sx={{ mt: 'auto', pt: 1.5, fontSize: '0.68rem', color: palette.inkLight, display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <Box
                            component="span"
                            sx={{
                                width: 6, height: 6, borderRadius: '50%', bgcolor: accent,
                                boxShadow: `0 0 0 3px ${rgba(accent, 0.16)}`,
                                animation: 'tfPulse 2.4s ease-in-out infinite',
                            }}
                        />
                        {hint}
                    </Typography>
                </Box>
            </Paper>
        </motion.div>
    );
};

/* ── meta pill + company avatar ───────────────────────────────────────────── */

const MetaPill = ({ label, tone = palette.inkLight, pulse = false, icon }) => (
    <Box
        sx={{
            display: 'inline-flex', alignItems: 'center', gap: 0.55,
            px: 1, py: 0.35, borderRadius: '9px', whiteSpace: 'nowrap',
            fontFamily: fontMono, fontSize: '0.6rem', fontWeight: 600,
            letterSpacing: '0.04em', textTransform: 'uppercase',
            color: tone, bgcolor: rgba(tone, 0.1), border: `1px solid ${rgba(tone, 0.2)}`,
        }}
    >
        {icon
            ? React.cloneElement(icon, { sx: { fontSize: 12 } })
            : <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: tone, animation: pulse ? 'tfPulse 2.4s ease-in-out infinite' : 'none' }} />}
        {label}
    </Box>
);

const LifecyclePill = ({ company }) => {
    const meta = LIFECYCLE_META[lifecycleOf(company)];
    return <MetaPill label={meta.label} tone={meta.tone} pulse={lifecycleOf(company) === 'active'} />;
};

const PaymentPill = ({ status }) => {
    const meta = PAYMENT_META[status] || { label: status || 'Unknown', tone: palette.inkLight };
    return <MetaPill label={meta.label} tone={meta.tone} />;
};

const SubscriptionPill = ({ status }) => {
    const meta = SUBSCRIPTION_META[status] || { label: status || 'None', tone: palette.inkLight };
    return <MetaPill label={meta.label} tone={meta.tone} />;
};

const TypePill = ({ type }) => {
    const meta = typeMeta(type);
    return <MetaPill label={meta.label} tone={meta.accent} />;
};

const CompanyAvatar = ({ company, size = 40, radius = 14 }) => {
    const accent = company?.isBlocked ? palette.inkLight : typeMeta(company?.companyType).accent;
    return (
        <Avatar
            src={company?.logo || company?.profileImage || ''}
            sx={{
                width: size, height: size, borderRadius: `${radius}px`, flexShrink: 0,
                fontFamily: fontMono, fontSize: size * 0.34, fontWeight: 700, color: '#fff',
                background: `linear-gradient(150deg, ${accent}, ${rgba(accent, 0.68)})`,
                boxShadow: `inset 0 1px 0 ${rgba('#FFFFFF', 0.4)}, 0 8px 18px -8px ${rgba(accent, 0.85)}`,
                filter: company?.isBlocked ? 'grayscale(0.6)' : 'none',
            }}
        >
            {(company?.companyName || 'C').charAt(0).toUpperCase()}
        </Avatar>
    );
};

/* ═══════════════════════════════════════════════════════════════════════════
   5 · CERTIFICATE
   The sheet below is intentionally OPAQUE — no backdrop-filter, no inset
   shadows, no CSS the html2canvas rasteriser silently drops.
   ═══════════════════════════════════════════════════════════════════════════ */

const CERT = {
    paper: '#FBF7F0',
    paperEdge: '#EFE6D8',
    ink: '#1A130E',
    inkSoft: '#6B5C4C',
    rust: 'var(--primary, #2E6F8E)',
    gold: '#D78B28',
    pine: '#2E8B69',
};

const certList = (...values) => Array.from(new Set(
    values.flatMap((value) => Array.isArray(value) ? value : (value === null || value === undefined || value === '' ? [] : [value]))
        .map((value) => typeof value === 'object'
            ? String(value.label || value.name || value.value || value.title || '').trim()
            : String(value).trim())
        .filter(Boolean)
));

const CertificateSheet = React.forwardRef(function CertificateSheet({ company, registrationNumber }, ref) {
    const subscription = company?.subscription;
    const companyLogo = company?.logo || company?.logoUrl || company?.companyLogo || company?.companyLogoUrl || company?.profileImage || '';
    const services = certList(
        company?.servicesProvided,
        company?.primaryServiceProvided,
        company?.secondaryServiceProvided,
        company?.primaryService,
        company?.secondaryService,
        company?.services,
        company?.specialization
    );
    const regionPriorities = certList(
        company?.regionPriorities,
        company?.primaryRegionAvailability,
        company?.secondaryRegionAvailability,
        company?.preferredRegion,
        company?.secondaryPreferredRegion,
        company?.primaryRegion,
        company?.secondaryRegion
    );
    const lookingFor = certList(
        company?.lookingFor,
        company?.primaryServiceBuying,
        company?.secondaryServiceBuying,
        company?.primaryConnectWith,
        company?.secondaryConnectWith,
        company?.otherConnectWith
    );

    return (
        <Box
            ref={ref}
            sx={{
                position: 'relative',
                width: '100%',
                background: `linear-gradient(150deg, ${CERT.paper} 0%, ${CERT.paperEdge} 100%)`,
                border: `3px solid ${rgba(CERT.rust, 0.28)}`,
                borderRadius: '20px',
                p: { xs: 3, sm: 5 },
                overflow: 'hidden',
            }}
        >
            <Box sx={{ position: 'absolute', inset: 10, border: `1.5px solid ${rgba(CERT.gold, 0.28)}`, borderRadius: '14px', pointerEvents: 'none' }} />
            <Box sx={{ position: 'absolute', inset: 16, border: `1px solid ${rgba(CERT.rust, 0.12)}`, borderRadius: '10px', pointerEvents: 'none' }} />

            {[
                { top: 18, left: 18, rotate: '0deg' },
                { top: 18, right: 18, rotate: '90deg' },
                { bottom: 18, right: 18, rotate: '180deg' },
                { bottom: 18, left: 18, rotate: '270deg' },
            ].map((pos, i) => {
                const { rotate, ...offset } = pos;
                return (
                    <Box key={i} sx={{ position: 'absolute', ...offset, width: 42, height: 42, transform: `rotate(${rotate})`, opacity: 0.42, pointerEvents: 'none' }}>
                        <svg width="42" height="42" viewBox="0 0 40 40" fill="none">
                            <path d="M0 40L0 20C0 8.95 8.95 0 20 0L40 0" stroke={CERT.rust} strokeWidth="2.5" />
                            <path d="M6 34L6 20C6 12.3 12.3 6 20 6L34 6" stroke={CERT.gold} strokeWidth="1.4" />
                        </svg>
                    </Box>
                );
            })}

            <Box sx={{ position: 'relative', textAlign: 'center' }}>
                <Box
                    sx={{
                        height: 3, width: '52%', mx: 'auto', mb: 3, borderRadius: 3,
                        background: `linear-gradient(90deg, ${rgba(CERT.rust, 0)}, ${CERT.rust}, ${CERT.gold}, ${CERT.pine}, ${rgba(CERT.pine, 0)})`,
                    }}
                />

                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', alignItems: 'center', gap: 2, mb: 2.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.1, justifySelf: 'start' }}>
                        <PlatformLogo size={60} radius={13} alt="TELCOFAIR" sx={{ boxShadow: '0 10px 24px rgba(25,57,78,.12)' }} />
                        <Box sx={{ textAlign: 'left', display: { xs: 'none', sm: 'block' } }}>
                            <Typography sx={{ fontFamily: fontDisplay, fontWeight: 800, fontSize: '.95rem', color: CERT.ink, letterSpacing: '-.025em' }}>TELCOFAIR</Typography>
                            <Typography sx={{ fontFamily: fontMono, fontSize: '.5rem', fontWeight: 700, color: CERT.inkSoft, letterSpacing: '.12em', textTransform: 'uppercase' }}>Network registry</Typography>
                        </Box>
                    </Box>

                    <Box sx={{ width: 1, height: 48, bgcolor: rgba(CERT.ink, .10), display: { xs: 'none', sm: 'block' } }} />

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.1, justifySelf: 'end' }}>
                        <Box sx={{ textAlign: 'right', display: { xs: 'none', sm: 'block' } }}>
                            <Typography sx={{ fontFamily: fontDisplay, fontWeight: 750, fontSize: '.9rem', color: CERT.ink, maxWidth: 230, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{company?.companyName || 'Company'}</Typography>
                            <Typography sx={{ fontFamily: fontMono, fontSize: '.5rem', fontWeight: 700, color: CERT.inkSoft, letterSpacing: '.1em', textTransform: 'uppercase' }}>Registered company</Typography>
                        </Box>
                        <Box sx={{ width: 60, height: 60, borderRadius: '13px', bgcolor: '#fff', border: `1px solid ${rgba(CERT.ink,.10)}`, boxShadow: '0 10px 24px rgba(25,57,78,.10)', overflow: 'hidden', display: 'grid', placeItems: 'center' }}>
                            {companyLogo ? (
                                <Box component="img" src={companyLogo} alt={`${company?.companyName || 'Company'} logo`} sx={{ width: '100%', height: '100%', objectFit: 'contain', p: '4%', bgcolor: '#fff' }} />
                            ) : (
                                <StorefrontIcon sx={{ color: CERT.rust, fontSize: 29 }} />
                            )}
                        </Box>
                    </Box>
                </Box>

                <Typography sx={{ fontFamily: fontMono, fontSize: '0.66rem', fontWeight: 600, letterSpacing: '0.36em', textTransform: 'uppercase', color: CERT.inkSoft, mb: 1 }}>
                    Certificate of Registration
                </Typography>
                <Typography sx={{ fontFamily: fontDisplay, fontWeight: 600, fontSize: { xs: '1.7rem', sm: '2.4rem' }, color: CERT.ink, lineHeight: 1.1, letterSpacing: '-0.03em' }}>
                    {company?.companyName || 'Company'}
                </Typography>
                <Typography sx={{ color: CERT.inkSoft, fontSize: '0.9rem', mt: 0.75 }}>
                    is an officially registered entity on the TelcoFair network
                </Typography>

                <Box sx={{ width: 76, height: 3, mx: 'auto', my: 3, borderRadius: 3, background: `linear-gradient(90deg, ${CERT.rust}, ${CERT.gold})` }} />

                <Box
                    sx={{
                        display: 'grid', gap: 1.5, mb: 3, mx: 'auto', maxWidth: 820,
                        gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(5, minmax(0, 1fr))' },
                    }}
                >
                    {[
                        { k: 'Registration №', v: registrationNumber, tone: CERT.rust, mono: true },
                        { k: 'Date issued', v: formatDate(company?.createdAt || company?.verificationDate) , tone: CERT.pine },
                        { k: 'Membership', v: company?.isActive ? 'Active' : 'Inactive', tone: company?.isActive ? CERT.pine : CERT.inkSoft },
                        { k: 'Verification', v: company?.isVerified ? 'Verified' : 'Pending', tone: company?.isVerified ? CERT.pine : CERT.gold },
                        { k: 'Type', v: typeMeta(company?.companyType).label, tone: CERT.gold },
                    ].map((cell) => (
                        <Box
                            key={cell.k}
                            sx={{
                                p: 1.75, borderRadius: '12px', textAlign: 'center',
                                background: '#FFFFFF', border: `1px solid ${rgba(CERT.ink, 0.08)}`,
                            }}
                        >
                            <Typography sx={{ fontFamily: fontMono, fontSize: '0.55rem', fontWeight: 600, letterSpacing: '0.12em', textTransform: 'uppercase', color: CERT.inkSoft }}>
                                {cell.k}
                            </Typography>
                            <Typography
                                sx={{
                                    mt: 0.6, fontWeight: 700, color: cell.tone,
                                    fontFamily: cell.mono ? fontMono : fontBody,
                                    fontSize: cell.mono ? '0.78rem' : '0.86rem',
                                    wordBreak: 'break-word',
                                }}
                            >
                                {cell.v}
                            </Typography>
                        </Box>
                    ))}
                </Box>

                {subscription?.packageName && (
                    <Box
                        sx={{
                            maxWidth: 520, mx: 'auto', mb: 3, px: 2, py: 1.5, borderRadius: '12px',
                            background: rgba(CERT.rust, 0.05), border: `1px solid ${rgba(CERT.rust, 0.16)}`,
                        }}
                    >
                        <Typography sx={{ fontSize: '0.82rem', color: CERT.inkSoft }}>
                            Subscription&nbsp;
                            <Box component="span" sx={{ fontWeight: 700, color: CERT.rust }}>{subscription.packageName}</Box>
                            <Box component="span" sx={{ ml: 1, fontWeight: 700, color: CERT.pine }}>{priceLabel(subscription)}</Box>
                        </Typography>
                    </Box>
                )}

                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', md: 'repeat(3,minmax(0,1fr))' },
                        gap: 1.25,
                        maxWidth: 860,
                        mx: 'auto',
                        mb: 3,
                    }}
                >
                    {[
                        { label: 'Services', values: services, tone: CERT.gold, empty: 'No services listed' },
                        { label: 'Region priorities', values: regionPriorities, tone: CERT.pine, empty: 'No region priorities listed' },
                        { label: 'Looking for', values: lookingFor, tone: CERT.rust, empty: 'No buying interests listed' },
                    ].map((section) => (
                        <Box key={section.label} sx={{ minHeight: 108, p: 1.55, borderRadius: '12px', textAlign: 'left', bgcolor: '#fff', border: `1px solid ${rgba(section.tone,.18)}`, boxShadow: `0 8px 20px ${rgba(section.tone,.06)}` }}>
                            <Typography sx={{ fontFamily: fontMono, fontSize: '.54rem', fontWeight: 800, color: section.tone, textTransform: 'uppercase', letterSpacing: '.12em', mb: .8 }}>{section.label}</Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: .45 }}>
                                {section.values.length ? section.values.map((value) => (
                                    <Chip
                                        key={`${section.label}-${value}`}
                                        label={value}
                                        size="small"
                                        sx={{
                                            height: 'auto',
                                            minHeight: 21,
                                            maxWidth: '100%',
                                            border: `1px solid ${rgba(section.tone,.16)}`,
                                            bgcolor: rgba(section.tone,.06),
                                            color: CERT.ink,
                                            fontWeight: 700,
                                            '& .MuiChip-label': { whiteSpace: 'normal', wordBreak: 'break-word', px: .75, py: .3, fontSize: '.56rem', lineHeight: 1.25 },
                                        }}
                                    />
                                )) : <Typography sx={{ fontSize: '.64rem', color: CERT.inkSoft, fontStyle: 'italic' }}>{section.empty}</Typography>}
                            </Box>
                        </Box>
                    ))}
                </Box>

                <Box
                    sx={{
                        pt: 2.5, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 3,
                        flexWrap: 'wrap', borderTop: `1px solid ${rgba(CERT.ink, 0.08)}`,
                    }}
                >
                    <Box sx={{ textAlign: 'center' }}>
                        <Typography sx={{ fontFamily: fontDisplay, fontWeight: 600, fontSize: '1rem', color: CERT.ink }}>
                            {company?.isVerified ? 'Verified & authentic' : 'Registered entity'}
                        </Typography>
                        <Typography sx={{ fontSize: '0.72rem', color: CERT.inkSoft }}>
                            Issued by the TelcoFair network
                        </Typography>
                    </Box>
                    <Box sx={{ width: 1, height: 32, bgcolor: rgba(CERT.ink, 0.1) }} />
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 30, height: 30, borderRadius: '50%', display: 'grid', placeItems: 'center', background: `linear-gradient(140deg, ${CERT.rust}, ${CERT.gold})` }}>
                            <CheckIcon sx={{ fontSize: 17, color: '#fff' }} />
                        </Box>
                        <Typography sx={{ fontFamily: fontMono, fontSize: '0.68rem', fontWeight: 700, color: CERT.ink }}>
                            {company?.isVerified ? 'DIGITALLY VERIFIED' : 'REGISTRATION RECORDED'}
                        </Typography>
                    </Box>
                </Box>

                <Box
                    sx={{
                        height: 3, width: '36%', mx: 'auto', mt: 3, borderRadius: 3,
                        background: `linear-gradient(90deg, ${rgba(CERT.pine, 0)}, ${CERT.pine}, ${CERT.rust}, ${rgba(CERT.rust, 0)})`,
                    }}
                />
            </Box>
        </Box>
    );
});

const mergeLegacyCertificateProfile = (company = {}, user = {}) => {
    const merged = { ...(company || {}) };
    const listFields = [
        'servicesProvided', 'primaryServiceProvided', 'secondaryServiceProvided',
        'regionPriorities', 'primaryRegionAvailability', 'secondaryRegionAvailability',
        'preferredRegion', 'secondaryPreferredRegion',
        'lookingFor', 'primaryServiceBuying', 'secondaryServiceBuying',
        'primaryConnectWith', 'secondaryConnectWith', 'otherConnectWith', 'specialization',
    ];
    listFields.forEach((field) => {
        if (!certList(merged[field]).length && certList(user?.[field]).length) merged[field] = user[field];
    });
    return merged;
};

const CertificateDialog = ({ open, company, onClose, onIssued }) => {
    const sheetRef = useRef(null);
    const [downloading, setDownloading] = useState(false);
    const [certificateCompany, setCertificateCompany] = useState(company || null);

    useEffect(() => {
        let active = true;
        setCertificateCompany(company || null);
        if (!open || !company) return () => { active = false; };
        const adminUid = company.adminId || company.ownerId || company.createdBy || '';
        if (!adminUid) return () => { active = false; };
        getDoc(doc(db, 'users', adminUid))
            .then((snap) => {
                if (!active || !snap.exists()) return;
                setCertificateCompany(mergeLegacyCertificateProfile(company, snap.data() || {}));
            })
            .catch((error) => console.warn('[certificate] legacy profile lookup failed:', error));
        return () => { active = false; };
    }, [open, company]);

    // stable across renders — the preview and the PDF always agree
    const registrationNumber = useMemo(() => derivedRegistrationNumber(certificateCompany || company), [certificateCompany, company]);

    const download = async () => {
        const node = sheetRef.current;
        if (!node || downloading) return;
        setDownloading(true);
        try {
            const canvas = await html2canvas(node, {
                scale: Math.min(window.devicePixelRatio * 1.5 || 2, 3),
                backgroundColor: CERT.paper,
                useCORS: true,
                logging: false,
            });

            const image = canvas.toDataURL('image/jpeg', 0.95);
            const pdf = new jsPDF({
                orientation: canvas.width >= canvas.height ? 'landscape' : 'portrait',
                unit: 'pt',
                format: 'a4',
            });
            const pageW = pdf.internal.pageSize.getWidth();
            const pageH = pdf.internal.pageSize.getHeight();
            const fit = Math.min((pageW - 48) / canvas.width, (pageH - 48) / canvas.height);
            const w = canvas.width * fit;
            const h = canvas.height * fit;
            pdf.addImage(image, 'JPEG', (pageW - w) / 2, (pageH - h) / 2, w, h);
            pdf.save(`TelcoFair_Certificate_${(certificateCompany?.companyName || company?.companyName || 'company').replace(/\s+/g, '_')}.pdf`);

            toast.success('Certificate downloaded');
            onIssued?.(registrationNumber);
        } catch (err) {
            console.error('[certificate] generation failed:', err);
            toast.error('Could not generate the certificate');
        } finally {
            setDownloading(false);
        }
    };

    if (!company) return null;

    return (
        <Dialog
            open={open}
            onClose={downloading ? undefined : onClose}
            maxWidth="lg"
            fullWidth
            TransitionComponent={Fade}
            slotProps={{ paper: {
                sx: {
                    ...liquid(palette.amber, { radius: 28 }),
                    background: `linear-gradient(160deg, ${rgba('#FFFFFF', 0.95)}, ${rgba(palette.canvas, 0.94)})`,
                    maxHeight: '94vh',
                },
            } }}
        >
            <DialogContent sx={{ p: { xs: 2, md: 3.5 } }}>
                <CertificateSheet ref={sheetRef} company={certificateCompany || company} registrationNumber={registrationNumber} />
            </DialogContent>
            <Box
                sx={{
                    px: 3, py: 2, display: 'flex', justifyContent: 'flex-end', gap: 1.25,
                    borderTop: `1px solid ${palette.hairlineSoft}`, background: rgba('#FFFFFF', 0.5),
                }}
            >
                <Button
                    onClick={onClose}
                    disabled={downloading}
                    sx={{ textTransform: 'none', fontWeight: 600, color: palette.inkSoft, borderRadius: '12px', px: 2 }}
                >
                    Close
                </Button>
                <Button
                    variant="contained"
                    disableElevation
                    onClick={download}
                    disabled={downloading}
                    startIcon={downloading ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <DownloadIcon sx={{ fontSize: 17 }} />}
                    sx={{
                        textTransform: 'none', fontWeight: 700, borderRadius: '14px', px: 3, py: 1, color: '#fff',
                        background: `linear-gradient(140deg, ${palette.pine}, #123829)`,
                        boxShadow: `inset 0 1px 0 ${rgba('#FFFFFF', 0.25)}, 0 12px 28px -12px ${rgba(palette.pine, 1)}`,
                    }}
                >
                    {downloading ? 'Generating…' : 'Download PDF'}
                </Button>
            </Box>
        </Dialog>
    );
};

/* ═══════════════════════════════════════════════════════════════════════════
   6 · CONFIRM DIALOG + DETAIL LINE (shared by delete + block/unblock)
   ═══════════════════════════════════════════════════════════════════════════ */

const ConfirmDialog = ({ open, onClose, onConfirm, tone, icon, title, body, confirmLabel, busy }) => (
    <Dialog
        open={open}
        onClose={busy ? undefined : onClose}
        maxWidth="xs"
        fullWidth
        TransitionComponent={Fade}
        slotProps={{ paper: {
            sx: {
                ...liquid(tone, { radius: 26 }),
                background: `linear-gradient(155deg, ${rgba('#FFFFFF', 0.95)}, ${rgba(tone, 0.1)})`,
            },
        } }}
    >
        <Box sx={{ p: 3, textAlign: 'center' }}>
            <Box sx={{ display: 'grid', placeItems: 'center', mb: 2 }}>
                <GlassIcon icon={icon} color={tone} size={54} />
            </Box>
            <Typography sx={{ fontFamily: fontDisplay, fontWeight: 600, fontSize: '1.2rem', color: palette.ink }}>
                {title}
            </Typography>
            <Typography sx={{ color: palette.inkSoft, fontSize: '0.86rem', mt: 1, mb: 2.75 }}>
                {body}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1.25 }}>
                <Button
                    fullWidth
                    onClick={onClose}
                    disabled={busy}
                    sx={{ borderRadius: '12px', textTransform: 'none', fontWeight: 600, color: palette.inkSoft }}
                >
                    Cancel
                </Button>
                <Button
                    fullWidth
                    variant="contained"
                    disableElevation
                    onClick={onConfirm}
                    disabled={busy}
                    startIcon={busy ? <CircularProgress size={15} sx={{ color: '#fff' }} /> : icon}
                    sx={{
                        borderRadius: '12px', textTransform: 'none', fontWeight: 700, color: '#fff',
                        background: `linear-gradient(140deg, ${tone}, ${rgba(tone, 0.75)})`,
                        boxShadow: `0 12px 26px -12px ${rgba(tone, 1)}`,
                        '&.Mui-disabled': { color: rgba('#FFFFFF', 0.7) },
                    }}
                >
                    {busy ? 'Working…' : confirmLabel}
                </Button>
            </Box>
        </Box>
    </Dialog>
);

/* one labelled row inside a detail panel */
const DetailLine = ({ icon, label, value, accent }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, py: 0.85 }}>
        <Box
            sx={{
                width: 28, height: 28, borderRadius: '9px', display: 'grid', placeItems: 'center', flexShrink: 0,
                color: accent, bgcolor: rgba(accent, 0.1), border: `1px solid ${rgba(accent, 0.16)}`,
                '& svg': { fontSize: 15 },
            }}
        >
            {icon}
        </Box>
        <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontFamily: fontMono, fontSize: '0.58rem', letterSpacing: '0.1em', textTransform: 'uppercase', color: palette.inkFaint }}>
                {label}
            </Typography>
            <Box sx={{ fontSize: '0.84rem', color: palette.ink, fontWeight: 500, overflowWrap: 'anywhere' }}>
                {value}
            </Box>
        </Box>
    </Box>
);

/* ═══════════════════════════════════════════════════════════════════════════
   7 · COMPANY DETAILS DIALOG
   ═══════════════════════════════════════════════════════════════════════════ */

const CompanyDetailsDialog = ({ open, company, onClose, onIssueCertificate }) => {
    const [tab, setTab] = useState(0);
    useEffect(() => { if (open) setTab(0); }, [open]);

    const registrationNumber = useMemo(() => derivedRegistrationNumber(company), [company]);

    if (!company) return null;
    const accent = typeMeta(company.companyType).accent;
    const subscription = company.subscription;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            TransitionComponent={Fade}
            slotProps={{ paper: {
                sx: {
                    ...liquid(accent, { radius: 28 }),
                    background: `linear-gradient(160deg, ${rgba('#FFFFFF', 0.95)}, ${rgba(palette.canvas, 0.92)} 62%, ${rgba(accent, 0.1)})`,
                    maxHeight: '92vh',
                },
            } }}
        >
            <Box
                sx={{
                    p: 2.75, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    gap: 2, flexWrap: 'wrap', borderBottom: `1px solid ${palette.hairlineSoft}`,
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0 }}>
                    <CompanyAvatar company={company} size={62} radius={20} />
                    <Box sx={{ minWidth: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            <Typography
                                sx={{ fontFamily: fontDisplay, fontWeight: 600, fontSize: '1.35rem', color: palette.ink, letterSpacing: '-0.025em' }}
                                noWrap
                            >
                                {company.companyName || 'Unnamed company'}
                            </Typography>
                            {company.isVerified && <VerifiedIcon sx={{ fontSize: 19, color: palette.pine }} />}
                        </Box>
                        <Typography sx={{ fontSize: '0.8rem', color: palette.inkLight }} noWrap>
                            {company.headquarters || 'Location not set'}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 0.75, mt: 0.9, flexWrap: 'wrap' }}>
                            <TypePill type={company.companyType} />
                            <LifecyclePill company={company} />
                        </Box>
                    </Box>
                </Box>
                <IconButton
                    onClick={onClose}
                    sx={{
                        width: 36, height: 36, borderRadius: '12px', color: palette.inkSoft,
                        bgcolor: rgba(palette.ink, 0.04),
                        transition: 'transform .3s ease, background .3s ease',
                        '&:hover': { bgcolor: rgba(palette.ink, 0.08), transform: 'rotate(90deg)' },
                    }}
                >
                    <CloseIcon sx={{ fontSize: 18 }} />
                </IconButton>
            </Box>

            <DialogContent sx={{ p: 2.75 }}>
                <Tabs
                    value={tab}
                    onChange={(_, v) => setTab(v)}
                    sx={{
                        minHeight: 40, mb: 2.5,
                        '& .MuiTab-root': {
                            minHeight: 40, textTransform: 'none', fontWeight: 600, fontSize: '0.82rem',
                            fontFamily: fontBody, color: palette.inkLight,
                            '&.Mui-selected': { color: palette.ink },
                        },
                        '& .MuiTabs-indicator': { height: 3, borderRadius: 3, backgroundColor: accent },
                    }}
                >
                    <Tab icon={<BusinessIcon sx={{ fontSize: 17 }} />} iconPosition="start" label="Overview" />
                    <Tab icon={<SubscriptionsIcon sx={{ fontSize: 17 }} />} iconPosition="start" label="Subscription" />
                    <Tab icon={<EmojiEventsIcon sx={{ fontSize: 17 }} />} iconPosition="start" label="Certificate" />
                </Tabs>

                {tab === 0 && (
                    <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}>
                        <GlassPanel accent={palette.rust} delay={0}>
                            <PanelHead icon={<BusinessIcon />} title="Profile" accent={palette.rust} dense />
                            <DetailLine icon={<BusinessIcon />} label="Type" accent={palette.rust} value={typeMeta(company.companyType).label} />
                            <DetailLine icon={<GroupsIcon />} label="Size" accent={palette.rust} value={company.companySize ? `${company.companySize} employees` : 'Not set'} />
                            <DetailLine icon={<LocationIcon />} label="Headquarters" accent={palette.rust} value={company.headquarters || 'Not set'} />
                            <DetailLine
                                icon={<LanguageIcon />}
                                label="Website"
                                accent={palette.rust}
                                value={
                                    company.website ? (
                                        <Box
                                            component="a"
                                            href={normaliseUrl(company.website)}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            sx={{ color: palette.teal, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                                        >
                                            {company.website}
                                        </Box>
                                    ) : 'Not set'
                                }
                            />
                            <DetailLine icon={<LocationIcon />} label="Regions" accent={palette.rust} value={(company.regions || []).join(', ') || 'Not set'} />
                        </GlassPanel>

                        <GlassPanel accent={palette.pine} delay={0.06}>
                            <PanelHead icon={<VerifiedUserIcon />} title="Status" accent={palette.pine} dense />
                            <DetailLine icon={<CheckCircleIcon />} label="Lifecycle" accent={palette.pine} value={<LifecyclePill company={company} />} />
                            <DetailLine icon={<PaymentIcon />} label="Payment" accent={palette.pine} value={<PaymentPill status={company.paymentStatus || 'not_required'} />} />
                            <DetailLine icon={<CalendarIcon />} label="Joined" accent={palette.pine} value={formatDate(company.createdAt)} />
                            <DetailLine icon={<VerifiedIcon />} label="Verified on" accent={palette.pine} value={company.isVerified ? formatDate(company.verificationDate) : 'Not verified'} />
                            {company.email && <DetailLine icon={<EmailIcon />} label="Email" accent={palette.pine} value={company.email} />}
                            {company.phone && <DetailLine icon={<PhoneIcon />} label="Phone" accent={palette.pine} value={`${company.dialCode || ''} ${company.phone}`.trim()} />}
                        </GlassPanel>

                        {(company.description || company.verificationNotes) && (
                            <Box sx={{ gridColumn: { md: '1 / -1' } }}>
                                <GlassPanel accent={palette.teal} delay={0.12}>
                                    <PanelHead icon={<NotesIcon />} title="Notes" accent={palette.teal} dense />
                                    {company.description && (
                                        <Typography sx={{ fontSize: '0.86rem', color: palette.inkSoft, lineHeight: 1.6 }}>
                                            {company.description}
                                        </Typography>
                                    )}
                                    {company.verificationNotes && (
                                        <Typography sx={{ mt: company.description ? 1.5 : 0, fontSize: '0.78rem', color: palette.inkLight, fontStyle: 'italic' }}>
                                            Verification: {company.verificationNotes}
                                        </Typography>
                                    )}
                                </GlassPanel>
                            </Box>
                        )}
                    </Box>
                )}

                {tab === 1 && (
                    subscription?.packageName || subscription?.status ? (
                        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}>
                            <GlassPanel accent={palette.plum} delay={0}>
                                <PanelHead icon={<PackageIcon />} title="Plan" accent={palette.plum} dense />
                                <Typography sx={{ fontFamily: fontDisplay, fontWeight: 600, fontSize: '1.5rem', color: palette.ink, letterSpacing: '-0.02em' }}>
                                    {subscription.packageName || 'Unnamed package'}
                                </Typography>
                                <Typography
                                    sx={{
                                        fontFamily: fontMono, fontWeight: 700, fontSize: '1.15rem', mt: 0.5,
                                        color: palette.rust, fontVariantNumeric: 'tabular-nums',
                                    }}
                                >
                                    {priceLabel(subscription)}
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 0.75, mt: 1.5, flexWrap: 'wrap' }}>
                                    <SubscriptionPill status={subscription.status || 'inactive'} />
                                    <MetaPill label={subscription.billingCycle || 'one-time'} tone={palette.indigo} />
                                </Box>
                            </GlassPanel>

                            <GlassPanel accent={palette.amber} delay={0.06}>
                                <PanelHead icon={<ReceiptIcon />} title="Billing" accent={palette.amber} dense />
                                <DetailLine icon={<CalendarIcon />} label="Subscribed" accent={palette.amber} value={formatDate(subscription.subscribedAt || subscription.assignedAt)} />
                                <DetailLine icon={<CalendarIcon />} label="Valid until" accent={palette.amber} value={subscription.validUntil ? formatDate(subscription.validUntil) : 'No expiry'} />
                                <DetailLine icon={<PaymentIcon />} label="Payment status" accent={palette.amber} value={<PaymentPill status={company.paymentStatus || 'not_required'} />} />
                                {company.paymentAmount != null && (
                                    <DetailLine
                                        icon={<ReceiptIcon />}
                                        label="Amount paid"
                                        accent={palette.amber}
                                        value={`${currencySymbol(company.paymentCurrency || subscription.currency || 'EUR')}${Number(company.paymentAmount).toLocaleString()}`}
                                    />
                                )}
                            </GlassPanel>
                        </Box>
                    ) : (
                        <EmptyState icon={<PackageIcon />} title="No subscription" text="This company has no plan assigned yet." />
                    )
                )}

                {tab === 2 && (
                    <Box sx={{ maxWidth: 520, mx: 'auto', textAlign: 'center', py: 1 }}>
                        <Box sx={{ display: 'grid', placeItems: 'center', mb: 2 }}>
                            <GlassIcon icon={<EmojiEventsIcon />} color={palette.amber} size={72} />
                        </Box>
                        <Typography sx={{ fontFamily: fontDisplay, fontWeight: 600, fontSize: '1.3rem', color: palette.ink }}>
                            Registration certificate
                        </Typography>
                        <Typography sx={{ color: palette.inkLight, fontSize: '0.86rem', mt: 0.75, mb: 2.5 }}>
                            Preview and download the official certificate for {company.companyName}.
                        </Typography>

                        <Box
                            sx={{
                                p: 2, borderRadius: '18px', textAlign: 'left',
                                background: rgba('#FFFFFF', 0.6), border: `1px solid ${palette.hairlineSoft}`,
                            }}
                        >
                            {[
                                ['Company', company.companyName],
                                ['Registration №', registrationNumber],
                                ['Date issued', formatDate(company.createdAt || company.verificationDate)],
                                ...(subscription?.packageName ? [['Package', `${subscription.packageName} · ${priceLabel(subscription)}`]] : []),
                            ].map(([k, v], i, arr) => (
                                <Box
                                    key={k}
                                    sx={{
                                        display: 'flex', justifyContent: 'space-between', gap: 2, py: 0.85,
                                        borderBottom: i < arr.length - 1 ? `1px solid ${palette.hairlineSoft}` : 'none',
                                    }}
                                >
                                    <Typography sx={{ fontFamily: fontMono, fontSize: '0.62rem', letterSpacing: '0.1em', textTransform: 'uppercase', color: palette.inkLight }}>
                                        {k}
                                    </Typography>
                                    <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: palette.ink, textAlign: 'right' }}>
                                        {v}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>

                        <Button
                            fullWidth
                            onClick={() => onIssueCertificate(company)}
                            startIcon={<EmojiEventsIcon sx={{ fontSize: 18 }} />}
                            sx={{
                                mt: 2.5, py: 1.15, borderRadius: '14px', textTransform: 'none', fontWeight: 700, color: '#fff',
                                background: `linear-gradient(140deg, ${palette.rust}, ${palette.rustDeep})`,
                                boxShadow: `inset 0 1px 0 ${rgba('#FFFFFF', 0.28)}, 0 14px 30px -14px ${rgba(palette.rust, 1)}`,
                                '&:hover': { background: `linear-gradient(140deg, ${palette.rustGlow}, ${palette.rust})` },
                            }}
                        >
                            View &amp; download certificate
                        </Button>
                        <Typography sx={{ mt: 1.25, fontFamily: fontMono, fontSize: '0.62rem', color: palette.inkFaint }}>
                            The number is derived from the company record — it never changes between views.
                        </Typography>
                    </Box>
                )}
            </DialogContent>
        </Dialog>
    );
};

/* ═══════════════════════════════════════════════════════════════════════════
   8 · ADD / EDIT COMPANY DIALOG  (local state — typing never re-renders the table)
   ═══════════════════════════════════════════════════════════════════════════ */

const EMPTY_FORM = {
    companyName: '',
    description: '',
    companyType: 'operator',
    companySize: '51-200',
    headquarters: '',
    website: '',
    regions: [],
    isActive: true,
    isVerified: false,
    verificationNotes: '',
};

const CompanyFormDialog = ({ open, mode, company, authorUid, onClose, onSaved }) => {
    const [form, setForm] = useState(EMPTY_FORM);
    const [saving, setSaving] = useState(false);
    const [touched, setTouched] = useState(false);

    const isEdit = mode === 'edit';

    useEffect(() => {
        if (!open) return;
        setTouched(false);
        setForm(
            isEdit && company
                ? {
                      companyName: company.companyName || '',
                      description: company.description || '',
                      companyType: company.companyType || 'operator',
                      companySize: company.companySize || '51-200',
                      headquarters: company.headquarters || '',
                      website: company.website || '',
                      regions: Array.isArray(company.regions) ? company.regions : [],
                      isActive: company.isActive !== false,
                      isVerified: company.isVerified === true,
                      verificationNotes: company.verificationNotes || '',
                  }
                : EMPTY_FORM
        );
    }, [open, isEdit, company]);

    const set = (patch) => setForm((prev) => ({ ...prev, ...patch }));

    const nameError = touched && !form.companyName.trim() ? 'Company name is required' : '';
    const websiteError =
        touched && form.website.trim() && !URL_RE.test(normaliseUrl(form.website)) ? 'Enter a valid URL' : '';
    const valid = Boolean(form.companyName.trim()) && !websiteError;

    const handleSave = async () => {
        setTouched(true);
        if (!valid || saving) {
            if (!form.companyName.trim()) toast.error('Company name is required');
            return;
        }

        setSaving(true);
        try {
            const shared = {
                companyName: form.companyName.trim(),
                description: form.description.trim(),
                companyType: form.companyType,
                companySize: form.companySize,
                headquarters: form.headquarters.trim(),
                website: normaliseUrl(form.website),
                regions: form.regions,
                isActive: form.isActive,
                isVerified: form.isVerified,
                verificationNotes: form.verificationNotes.trim(),
                updatedAt: serverTimestamp(),
            };

            if (isEdit) {
                const wasVerified = company.isVerified === true;
                await updateDoc(doc(db, 'companies', company.id), {
                    ...shared,
                    // stamp the date only on the transition — editing a name should
                    // never reset when the company was verified
                    ...(form.isVerified && !wasVerified
                        ? { verificationDate: serverTimestamp(), verificationStatus: 'approved', verifiedBy: authorUid || '' }
                        : {}),
                    ...(!form.isVerified && wasVerified ? { verificationStatus: 'revoked' } : {}),
                });
                toast.success('Company updated');
            } else {
                const id = `company_${Date.now()}`;
                await setDoc(doc(db, 'companies', id), {
                    ...shared,
                    companyId: id,
                    isBlocked: false,
                    paymentStatus: 'not_required',
                    // honours the toggle instead of silently force-verifying
                    verificationStatus: form.isVerified ? 'approved' : 'pending',
                    ...(form.isVerified ? { verificationDate: serverTimestamp(), verifiedBy: authorUid || '' } : {}),
                    registrationNumber: derivedRegistrationNumber({ id, companyName: form.companyName.trim() }),
                    subscription: {
                        packageId: null,
                        packageName: null,
                        price: 0,
                        currency: 'EUR',
                        billingCycle: 'one-time',
                        status: 'inactive',
                        assignedAt: serverTimestamp(),
                        assignedBy: authorUid || '',
                    },
                    servicePriorities: {},
                    teamMembers: [],
                    metrics: {
                        profileVisits: 0,
                        meetingRequestsReceived: 0,
                        meetingsAccepted: 0,
                        meetingsCompleted: 0,
                        responseRate: 0,
                        engagementScore: 0,
                    },
                    createdAt: serverTimestamp(),
                    createdBy: authorUid || '',
                });
                toast.success('Company created');
            }

            onSaved();
            onClose();
        } catch (err) {
            console.error('[companies] save failed:', err);
            toast.error(err.code === 'permission-denied' ? 'You do not have permission to do that' : `Failed to save: ${err.message}`);
        } finally {
            setSaving(false);
        }
    };

    const accent = isEdit ? palette.amber : palette.rust;

    return (
        <Dialog
            open={open}
            onClose={saving ? undefined : onClose}
            maxWidth="sm"
            fullWidth
            TransitionComponent={Fade}
            slotProps={{ paper: {
                sx: {
                    ...liquid(accent, { radius: 28 }),
                    background: `linear-gradient(160deg, ${rgba('#FFFFFF', 0.95)}, ${rgba(palette.canvas, 0.92)} 62%, ${rgba(accent, 0.1)})`,
                    maxHeight: '92vh',
                },
            } }}
        >
            <Box
                sx={{
                    p: 2.75, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    borderBottom: `1px solid ${palette.hairlineSoft}`,
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75 }}>
                    <GlassIcon icon={isEdit ? <EditIcon /> : <AddIcon />} color={accent} size={46} />
                    <Box>
                        <Typography sx={{ fontFamily: fontDisplay, fontWeight: 600, fontSize: '1.2rem', color: palette.ink, letterSpacing: '-0.02em' }}>
                            {isEdit ? 'Edit company' : 'Add company'}
                        </Typography>
                        <Typography sx={{ color: palette.inkLight, fontSize: '0.78rem' }}>
                            {isEdit ? 'Update the profile, regions and verification' : 'Creates a directory record for the exhibitor'}
                        </Typography>
                    </Box>
                </Box>
                <IconButton
                    onClick={onClose}
                    disabled={saving}
                    sx={{
                        width: 36, height: 36, borderRadius: '12px', color: palette.inkSoft,
                        bgcolor: rgba(palette.ink, 0.04),
                        transition: 'transform .3s ease, background .3s ease',
                        '&:hover': { bgcolor: rgba(palette.ink, 0.08), transform: 'rotate(90deg)' },
                    }}
                >
                    <CloseIcon sx={{ fontSize: 18 }} />
                </IconButton>
            </Box>

            <DialogContent sx={{ p: 2.75, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                    fullWidth size="small" label="Company name" required
                    value={form.companyName}
                    onChange={(e) => set({ companyName: e.target.value })}
                    error={Boolean(nameError)}
                    helperText={nameError || ' '}
                    sx={glassField(accent)}
                />

                <TextField
                    fullWidth size="small" label="Description" multiline rows={3}
                    placeholder="What does this company do?"
                    value={form.description}
                    onChange={(e) => set({ description: e.target.value })}
                    inputProps={{ maxLength: 600 }}
                    helperText={
                        <Box component="span" sx={{ display: 'block', textAlign: 'right', fontFamily: fontMono, fontSize: '0.65rem' }}>
                            {form.description.length}/600
                        </Box>
                    }
                    sx={glassField(accent)}
                />

                <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
                    <FormControl fullWidth size="small" sx={glassField(accent)}>
                        <InputLabel>Type</InputLabel>
                        <Select value={form.companyType} label="Type" onChange={(e) => set({ companyType: e.target.value })}>
                            {COMPANY_TYPES.map((t) => (
                                <MenuItem key={t.value} value={t.value}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: t.accent }} />
                                        {t.label}
                                    </Box>
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl fullWidth size="small" sx={glassField(accent)}>
                        <InputLabel>Size</InputLabel>
                        <Select value={form.companySize} label="Size" onChange={(e) => set({ companySize: e.target.value })}>
                            {COMPANY_SIZES.map((s) => (
                                <MenuItem key={s} value={s}>{s} employees</MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <TextField
                        fullWidth size="small" label="Headquarters" placeholder="City, Country"
                        value={form.headquarters}
                        onChange={(e) => set({ headquarters: e.target.value })}
                        helperText=" "
                        sx={glassField(accent)}
                    />

                    <TextField
                        fullWidth size="small" label="Website" placeholder="example.com"
                        value={form.website}
                        onChange={(e) => set({ website: e.target.value })}
                        error={Boolean(websiteError)}
                        helperText={websiteError || 'https:// is added automatically'}
                        sx={glassField(accent)}
                    />
                </Box>

                <FormControl fullWidth size="small" sx={glassField(accent)}>
                    <InputLabel>Regions</InputLabel>
                    <Select
                        multiple
                        value={form.regions}
                        label="Regions"
                        onChange={(e) => set({ regions: typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value })}
                        renderValue={(selected) => (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {selected.map((r) => <MetaPill key={r} label={r} tone={palette.teal} />)}
                            </Box>
                        )}
                    >
                        {REGIONS.map((r) => (
                            <MenuItem key={r} value={r}>{r}</MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    {[
                        { key: 'isActive', label: 'Active', tone: palette.pine },
                        { key: 'isVerified', label: 'Verified', tone: palette.plum },
                    ].map((t) => (
                        <FormControlLabel
                            key={t.key}
                            control={
                                <Switch
                                    checked={form[t.key]}
                                    onChange={(e) => set({ [t.key]: e.target.checked })}
                                    sx={{
                                        '& .MuiSwitch-switchBase.Mui-checked': { color: t.tone },
                                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: t.tone },
                                    }}
                                />
                            }
                            label={<Typography sx={{ fontSize: '0.85rem', fontWeight: 500, color: palette.ink }}>{t.label}</Typography>}
                        />
                    ))}
                </Box>

                <AnimatePresence initial={false}>
                    {(isEdit || !form.isVerified) && (
                        <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            transition={{ duration: 0.25 }}
                            style={{ overflow: 'hidden' }}
                        >
                            <TextField
                                fullWidth size="small" label="Verification notes" multiline rows={2}
                                value={form.verificationNotes}
                                onChange={(e) => set({ verificationNotes: e.target.value })}
                                sx={{ mt: 0.5, ...glassField(accent) }}
                            />
                        </motion.div>
                    )}
                </AnimatePresence>
            </DialogContent>

            <Box
                sx={{
                    px: 2.75, py: 2, display: 'flex', justifyContent: 'flex-end', gap: 1.25,
                    borderTop: `1px solid ${palette.hairlineSoft}`, background: rgba('#FFFFFF', 0.45), backdropFilter: 'blur(8px)',
                }}
            >
                <Button
                    onClick={onClose}
                    disabled={saving}
                    sx={{ textTransform: 'none', fontWeight: 600, color: palette.inkSoft, borderRadius: '12px', px: 2 }}
                >
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    disableElevation
                    onClick={handleSave}
                    disabled={saving}
                    startIcon={saving ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : isEdit ? <SaveIcon sx={{ fontSize: 17 }} /> : <AddIcon sx={{ fontSize: 17 }} />}
                    sx={{
                        textTransform: 'none', fontWeight: 700, borderRadius: '14px', px: 2.75, py: 1, color: '#fff',
                        background: `linear-gradient(140deg, ${accent}, ${rgba(accent, 0.72)})`,
                        boxShadow: `inset 0 1px 0 ${rgba('#FFFFFF', 0.28)}, 0 12px 28px -12px ${rgba(accent, 0.95)}`,
                        '&.Mui-disabled': { background: rgba(palette.ink, 0.08), color: palette.inkFaint, boxShadow: 'none' },
                    }}
                >
                    {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Create company'}
                </Button>
            </Box>
        </Dialog>
    );
};

/* ═══════════════════════════════════════════════════════════════════════════
   9 · MAIN
   ═══════════════════════════════════════════════════════════════════════════ */

const Companies = () => {
    const navigate = useNavigate();
    // AuthContext exposes the account as `currentUser` in most of the app and as
    // `user` in a few places — accept either rather than silently getting undefined.
    const authValue = useAuth() || {};
    const account = authValue.currentUser || authValue.user || null;
    const authLoading = Boolean(authValue.loading);
    const signedIn = authValue.isAuthenticated ?? Boolean(account);
    const authorUid = account?.uid || null;

    const [companies, setCompanies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState(null);

    const [search, setSearch] = useState('');
    const [filters, setFilters] = useState({ type: '', status: '', payment: '', subscription: '', region: '' });
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const [formState, setFormState] = useState({ open: false, mode: 'add', company: null });
    const [details, setDetails] = useState({ open: false, company: null });
    const [certificate, setCertificate] = useState({ open: false, company: null });
    const [confirm, setConfirm] = useState({ open: false, kind: null, company: null });
    const [busy, setBusy] = useState(false);

    /* ── data ──────────────────────────────────────────────────────────────── */

    const loadCompanies = useCallback(async (silent = false) => {
        if (!signedIn) { setLoading(false); return; }
        if (silent) setRefreshing(true); else setLoading(true);
        setError(null);

        try {
            // deliberately unordered: an orderBy('createdAt') query silently DROPS
            // documents missing that field, which would hide real companies.
            const snap = await getDocs(collection(db, 'companies'));
            const list = [];
            snap.forEach((d) => {
                if (d.id.startsWith('_')) return;
                const data = d.data() || {};
                if (data.isDeleted === true || data.archived === true) return;
                list.push({ id: d.id, ...data });
            });
            list.sort((a, b) => (toDate(b.createdAt)?.getTime() || 0) - (toDate(a.createdAt)?.getTime() || 0));
            setCompanies(list);
        } catch (err) {
            console.error('[companies] load failed:', err);
            setError('Unable to load companies right now.');
            toast.error('Unable to load companies right now.');
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, [signedIn]);

    useEffect(() => {
        if (authLoading) return;
        loadCompanies();
    }, [authLoading, loadCompanies]);

    /* ── derived ───────────────────────────────────────────────────────────── */

    const filtered = useMemo(() => {
        const term = search.trim().toLowerCase();
        return companies.filter((c) => {
            if (filters.type && c.companyType !== filters.type) return false;
            if (filters.status && lifecycleOf(c) !== filters.status) return false;
            if (filters.payment && (c.paymentStatus || 'not_required') !== filters.payment) return false;
            if (filters.subscription && (c.subscription?.status || 'inactive') !== filters.subscription) return false;
            if (filters.region && !(c.regions || []).includes(filters.region)) return false;
            if (!term) return true;
            return [
                c.companyName, c.description, c.headquarters, c.website,
                c.email, c.phone, c.subscription?.packageName,
            ].some((field) => String(field || '').toLowerCase().includes(term));
        });
    }, [companies, search, filters]);

    useEffect(() => {
        const maxPage = Math.max(Math.ceil(filtered.length / rowsPerPage) - 1, 0);
        if (page > maxPage) setPage(maxPage);
    }, [filtered.length, rowsPerPage, page]);

    const paginated = useMemo(
        () => filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
        [filtered, page, rowsPerPage]
    );

    const stats = useMemo(() => {
        const since = Date.now() - 30 * 86400000;
        return {
            total: companies.length,
            active: companies.filter((c) => lifecycleOf(c) === 'active').length,
            pending: companies.filter((c) => lifecycleOf(c) === 'pending').length,
            blocked: companies.filter((c) => c.isBlocked).length,
            activeSubs: companies.filter((c) => c.subscription?.status === 'active').length,
            paymentReview: companies.filter((c) => ['review', 'pending'].includes(c.paymentStatus)).length,
            new30: companies.filter((c) => (toDate(c.createdAt)?.getTime() || 0) >= since).length,
            revenue: companies.reduce((sum, c) => {
                const paid = ['paid', 'approved'].includes(c.paymentStatus);
                return paid ? sum + (Number(c.paymentAmount ?? c.subscription?.price) || 0) : sum;
            }, 0),
        };
    }, [companies]);

    const statCards = useMemo(() => [
        {
            label: 'Companies', value: stats.total, icon: <BusinessIcon  key="config-1840-0-2" />, accent: palette.rust,
            delta: stats.new30 > 0 ? { label: `+${compact(stats.new30)}`, trend: 'up' } : null,
            hint: stats.new30 > 0 ? `${compact(stats.new30)} added in 30d` : 'Directory total',
        },
        {
            label: 'Active', value: stats.active, icon: <CheckCircleIcon  key="config-1845-1-2" />, accent: palette.pine,
            hint: stats.total ? `${Math.round((stats.active / stats.total) * 100)}% of directory` : 'Nothing yet',
        },
        {
            label: 'Awaiting review', value: stats.pending, icon: <VerifiedUserIcon  key="config-1849-2-2" />, accent: palette.warning,
            hint: stats.pending ? 'Needs verification' : 'All verified',
        },
        {
            label: 'Active plans', value: stats.activeSubs, icon: <SubscriptionsIcon  key="config-1853-3-2" />, accent: palette.plum,
            hint: 'Live subscriptions',
        },
        {
            label: 'Payments open', value: stats.paymentReview, icon: <ReceiptIcon  key="config-1857-4-2" />, accent: palette.amber,
            hint: stats.paymentReview ? 'Pending or in review' : 'Nothing outstanding',
        },
        {
            label: 'Blocked', value: stats.blocked, icon: <BlockIcon  key="config-1861-5-2" />, accent: palette.danger,
            hint: stats.blocked ? 'Access revoked' : 'None blocked',
        },
    ], [stats]);

    /* ── mutations ─────────────────────────────────────────────────────────── */

    const patchLocal = (id, patch) =>
        setCompanies((prev) => prev.map((c) => (c.id === id ? { ...c, ...patch } : c)));

    const verifyCompany = async (company) => {
        try {
            await updateDoc(doc(db, 'companies', company.id), {
                isVerified: true,
                verificationStatus: 'approved',
                verificationDate: serverTimestamp(),
                verifiedBy: authorUid || '',
                updatedAt: serverTimestamp(),
            });
            patchLocal(company.id, { isVerified: true, verificationStatus: 'approved', verificationDate: new Date() });
            toast.success(`${company.companyName} verified`);
        } catch (err) {
            console.error('[companies] verify failed:', err);
            toast.error('Failed to verify company');
        }
    };

    const confirmBlock = async () => {
        const target = confirm.company;
        if (!target) return;
        const next = !target.isBlocked;
        setBusy(true);
        try {
            await updateDoc(doc(db, 'companies', target.id), { isBlocked: next, updatedAt: serverTimestamp() });
            patchLocal(target.id, { isBlocked: next });
            toast.success(next ? 'Company blocked' : 'Company unblocked');
            setConfirm({ open: false, kind: null, company: null });
        } catch (err) {
            console.error('[companies] block failed:', err);
            toast.error('Failed to update status');
        } finally {
            setBusy(false);
        }
    };

    const confirmDelete = async () => {
        const target = confirm.company;
        if (!target) return;
        setBusy(true);
        try {
            await updateDoc(doc(db, 'companies', target.id), {
                isDeleted: true,
                archived: true,
                isActive: false,
                isBlocked: true,
                deletedAt: serverTimestamp(),
                deletedBy: authorUid || '',
                updatedAt: serverTimestamp(),
            });
            setCompanies((prev) => prev.filter((c) => c.id !== target.id));
            toast.success('Company archived and access disabled');
            setConfirm({ open: false, kind: null, company: null });
        } catch (err) {
            console.error('[companies] delete failed:', err);
            toast.error(err.code === 'permission-denied' ? 'You do not have permission to delete companies' : 'Failed to delete company');
        } finally {
            setBusy(false);
        }
    };

    // freeze the number the operator just downloaded so it can never drift
    const persistCertificate = async (company, registrationNumber) => {
        if (company.registrationNumber === registrationNumber && company.certificateGenerated) return;
        try {
            await updateDoc(doc(db, 'companies', company.id), {
                registrationNumber,
                certificateGenerated: true,
                certificateGeneratedAt: serverTimestamp(),
                updatedAt: serverTimestamp(),
            });
            patchLocal(company.id, { registrationNumber, certificateGenerated: true });
        } catch (err) {
            console.warn('[companies] could not persist certificate number:', err.message);
        }
    };

    const filtersActive = Boolean(search || Object.values(filters).some(Boolean));
    const clearFilters = () => {
        setSearch('');
        setFilters({ type: '', status: '', payment: '', subscription: '', region: '' });
        setPage(0);
    };
    const setFilter = (key, value) => { setFilters((f) => ({ ...f, [key]: value })); setPage(0); };

    const headCellSx = {
        borderBottom: `1px solid ${palette.hairline}`,
        fontFamily: fontMono, fontSize: '0.6rem', fontWeight: 600,
        letterSpacing: '0.12em', textTransform: 'uppercase', color: palette.inkLight,
        background: 'transparent', py: 1.5,
    };
    const bodyCellSx = { borderBottom: `1px solid ${palette.hairlineSoft}`, py: 1.35 };

    const shell = {
        minHeight: '100vh', position: 'relative', fontFamily: fontBody, color: palette.ink,
        background: `linear-gradient(180deg, ${palette.canvas} 0%, ${palette.canvasDeep} 100%)`,
        '&::before': {
            content: '""', position: 'fixed', inset: 0, zIndex: 0, pointerEvents: 'none', opacity: 0.4,
            backgroundImage:
                'url("data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 width=%27140%27 height=%27140%27%3E%3Cfilter id=%27n%27%3E%3CfeTurbulence type=%27fractalNoise%27 baseFrequency=%270.85%27 numOctaves=%272%27 stitchTiles=%27stitch%27/%3E%3C/filter%3E%3Crect width=%27100%25%27 height=%27100%25%27 filter=%27url(%23n)%27 opacity=%270.03%27/%3E%3C/svg%3E")',
            animation: 'tfGrain 8s steps(6) infinite',
        },
    };

    /* ── auth gates ────────────────────────────────────────────────────────── */

    if (authLoading) {
        return (
            <Box sx={shell}>
                <style>{FONT_IMPORT}</style>
                <LiquidBackdrop />
                <Box sx={{ position: 'relative', zIndex: 2 }}></Box>
                <Container maxWidth="sm" sx={{ position: 'relative', zIndex: 1, py: 10 }}>
                    <GlassPanel accent={palette.rust}>
                        <Box sx={{ display: 'grid', placeItems: 'center', gap: 2, py: 5 }}>
                            <CircularProgress sx={{ color: palette.rust }} />
                            <Typography sx={{ color: palette.inkLight, fontSize: '0.88rem' }}>
                                Checking your session…
                            </Typography>
                        </Box>
                    </GlassPanel>
                </Container>
            </Box>
        );
    }

    if (!signedIn) {
        return (
            <Box sx={shell}>
                <style>{FONT_IMPORT}</style>
                <LiquidBackdrop />
                <Box sx={{ position: 'relative', zIndex: 2 }}></Box>
                <Container maxWidth="sm" sx={{ position: 'relative', zIndex: 1, py: 10 }}>
                    <GlassPanel accent={palette.rust}>
                        <EmptyState
                            icon={<BusinessIcon />}
                            title="Sign in required"
                            text="You need an active session to browse the company directory."
                        />
                        <Button
                            onClick={() => navigate('/login')}
                            sx={{
                                mx: 'auto', mb: 2, px: 3, py: 1, borderRadius: '14px', textTransform: 'none',
                                fontWeight: 700, color: '#fff',
                                background: `linear-gradient(140deg, ${palette.rust}, ${palette.rustDeep})`,
                                boxShadow: `0 14px 30px -14px ${rgba(palette.rust, 1)}`,
                            }}
                        >
                            Go to login
                        </Button>
                    </GlassPanel>
                </Container>
            </Box>
        );
    }

    /* ═══════════════════════════════════════════════════════════════════════
       RENDER
       ═══════════════════════════════════════════════════════════════════════ */

    return (
        <Box sx={shell}>
            <style>{FONT_IMPORT}</style>
            <LiquidBackdrop />
            <Box sx={{ position: 'relative', zIndex: 2 }}></Box>

            <Container
                maxWidth={false}
                disableGutters
                sx={{ position: 'relative', zIndex: 1, px: { xs: 2, sm: 3, lg: 5 }, py: { xs: 3, md: 4 } }}
            >
                {/* ── hero ─────────────────────────────────────────────────── */}
                <motion.div
                    initial={{ opacity: 0, y: -14 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 3.5 }}>
                        <Box sx={{ minWidth: 0 }}>
                            <Typography
                                sx={{
                                    fontFamily: fontMono, fontSize: '0.64rem', fontWeight: 500,
                                    letterSpacing: '0.22em', textTransform: 'uppercase', color: palette.rust, mb: 0.75,
                                }}
                            >
                                TelcoFair · Directory
                            </Typography>
                            <Typography
                                sx={{
                                    fontFamily: fontDisplay, fontWeight: 600,
                                    fontSize: { xs: '1.9rem', md: '2.6rem' },
                                    letterSpacing: '-0.035em', lineHeight: 1.05, color: palette.ink,
                                }}
                            >
                                The{' '}
                                <Box
                                    component="span"
                                    sx={{
                                        fontStyle: 'italic',
                                        background: `linear-gradient(120deg, ${palette.rust}, ${palette.amber})`,
                                        WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
                                    }}
                                >
                                    companies
                                </Box>
                            </Typography>
                            <Typography sx={{ color: palette.inkSoft, fontSize: '0.92rem', mt: 0.75, maxWidth: 560 }}>
                                Every exhibitor, operator and vendor on the platform — verification, plans and certificates in one view.
                            </Typography>

                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.75, flexWrap: 'wrap' }}>
                                <Box
                                    sx={{
                                        display: 'inline-flex', alignItems: 'center', gap: 0.75, px: 1.25, height: 30,
                                        borderRadius: '999px', border: `1px solid ${rgba(palette.pine, 0.22)}`,
                                        background: `linear-gradient(140deg, ${rgba('#FFFFFF', 0.75)}, ${rgba(palette.pine, 0.1)})`,
                                        backdropFilter: 'blur(10px)',
                                    }}
                                >
                                    <LiveIcon sx={{ fontSize: 9, color: palette.pine, animation: 'tfPulse 2s ease-in-out infinite' }} />
                                    <Typography sx={{ fontFamily: fontMono, fontSize: '0.66rem', fontWeight: 600, color: palette.pine }}>
                                        {compact(stats.active)} ACTIVE
                                    </Typography>
                                </Box>
                                {stats.paymentReview > 0 && (
                                    <Box
                                        sx={{
                                            display: 'inline-flex', alignItems: 'center', gap: 0.75, px: 1.25, height: 30,
                                            borderRadius: '999px', border: `1px solid ${rgba(palette.amber, 0.28)}`,
                                            background: `linear-gradient(140deg, ${rgba('#FFFFFF', 0.75)}, ${rgba(palette.amber, 0.12)})`,
                                            backdropFilter: 'blur(10px)',
                                        }}
                                    >
                                        <ReceiptIcon sx={{ fontSize: 13, color: palette.amber }} />
                                        <Typography sx={{ fontFamily: fontMono, fontSize: '0.66rem', fontWeight: 600, color: palette.amber }}>
                                            {compact(stats.paymentReview)} PAYMENTS OPEN
                                        </Typography>
                                    </Box>
                                )}
                                {stats.revenue > 0 && (
                                    <Box
                                        sx={{
                                            display: 'inline-flex', alignItems: 'center', gap: 0.75, px: 1.25, height: 30,
                                            borderRadius: '999px', border: `1px solid ${rgba(palette.indigo, 0.24)}`,
                                            background: `linear-gradient(140deg, ${rgba('#FFFFFF', 0.75)}, ${rgba(palette.indigo, 0.1)})`,
                                            backdropFilter: 'blur(10px)',
                                        }}
                                    >
                                        <PaymentIcon sx={{ fontSize: 13, color: palette.indigo }} />
                                        <Typography sx={{ fontFamily: fontMono, fontSize: '0.66rem', fontWeight: 600, color: palette.indigo }}>
                                            {compact(stats.revenue)} COLLECTED
                                        </Typography>
                                    </Box>
                                )}
                            </Box>

                            {error && (
                                <Box
                                    sx={{
                                        mt: 2, px: 1.75, py: 1.25, borderRadius: '14px', maxWidth: 560,
                                        background: rgba(palette.danger, 0.07), border: `1px solid ${rgba(palette.danger, 0.22)}`,
                                    }}
                                >
                                    <Typography sx={{ fontSize: '0.78rem', color: palette.danger, fontWeight: 500 }}>
                                        {error}
                                    </Typography>
                                </Box>
                            )}
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap' }}>
                            <Button
                                onClick={() => loadCompanies(true)}
                                disabled={refreshing}
                                startIcon={<RefreshIcon sx={{ animation: refreshing ? 'tfSpin 1s linear infinite' : 'none' }} />}
                                sx={{
                                    px: 2.25, py: 1, borderRadius: '14px', textTransform: 'none', fontWeight: 600,
                                    fontSize: '0.82rem', color: palette.inkSoft,
                                    border: `1px solid ${rgba('#FFFFFF', 0.7)}`,
                                    background: `linear-gradient(140deg, ${rgba('#FFFFFF', 0.8)}, ${rgba('#FFFFFF', 0.45)})`,
                                    backdropFilter: 'blur(14px)',
                                    boxShadow: `inset 0 1px 0 ${rgba('#FFFFFF', 0.9)}, 0 8px 20px -12px ${rgba(palette.ink, 0.5)}`,
                                    '&:hover': { color: palette.ink, background: rgba('#FFFFFF', 0.9) },
                                }}
                            >
                                {refreshing ? 'Refreshing' : 'Refresh'}
                            </Button>
                            <Button
                                onClick={() => setFormState({ open: true, mode: 'add', company: null })}
                                startIcon={<AddIcon />}
                                sx={{
                                    px: 2.5, py: 1, borderRadius: '14px', textTransform: 'none', fontWeight: 700,
                                    fontSize: '0.82rem', color: '#fff',
                                    background: `linear-gradient(140deg, ${palette.rust}, ${palette.rustDeep})`,
                                    border: `1px solid ${rgba('#FFFFFF', 0.25)}`,
                                    boxShadow: `inset 0 1px 0 ${rgba('#FFFFFF', 0.35)}, 0 14px 30px -14px ${rgba(palette.rust, 1)}`,
                                    '&:hover': { background: `linear-gradient(140deg, ${palette.rustGlow}, ${palette.rust})` },
                                }}
                            >
                                Add company
                            </Button>
                        </Box>
                    </Box>
                </motion.div>

                {/* ── stats ────────────────────────────────────────────────── */}
                <Box
                    sx={{
                        display: 'grid', gap: 2, mb: 3,
                        gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(3, 1fr)', xl: 'repeat(6, 1fr)' },
                    }}
                >
                    {statCards.map((s, i) => (
                        <StatCard key={s.label} {...s} delay={i * 0.06} loading={loading} />
                    ))}
                </Box>

                {/* ── filters ──────────────────────────────────────────────── */}
                <GlassPanel accent={palette.rust} delay={0.1} sx={{ mb: 2.5 }} padding={{ xs: 2, md: 2.25 }}>
                    <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
                        <TextField
                            size="small"
                            placeholder="Search name, location, website, plan…"
                            value={search}
                            onChange={(e) => { setSearch(e.target.value); setPage(0); }}
                            sx={{ flex: '1 1 260px', minWidth: 220, ...glassField(palette.rust) }}
                            slotProps={{ input: {
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={{ fontSize: 18, color: palette.inkFaint }} />
                                    </InputAdornment>
                                ),
                            } }}
                        />

                        {[
                            {
                                key: 'type', label: 'Type', width: 150,
                                options: [['', 'All types'], ...COMPANY_TYPES.map((t) => [t.value, t.label])],
                            },
                            {
                                key: 'status', label: 'Status', width: 140,
                                options: [['', 'All statuses'], ...Object.entries(LIFECYCLE_META).map(([k, m]) => [k, m.label])],
                            },
                            {
                                key: 'payment', label: 'Payment', width: 150,
                                options: [['', 'All payments'], ...Object.entries(PAYMENT_META).map(([k, m]) => [k, m.label])],
                            },
                            {
                                key: 'subscription', label: 'Plan', width: 150,
                                options: [['', 'All plans'], ...Object.entries(SUBSCRIPTION_META).map(([k, m]) => [k, m.label])],
                            },
                            {
                                key: 'region', label: 'Region', width: 150,
                                options: [['', 'All regions'], ...REGIONS.map((r) => [r, r])],
                            },
                        ].map((f) => (
                            <FormControl key={f.key} size="small" sx={{ minWidth: f.width, ...glassField(palette.rust) }}>
                                <InputLabel>{f.label}</InputLabel>
                                <Select
                                    value={filters[f.key]}
                                    label={f.label}
                                    onChange={(e) => setFilter(f.key, e.target.value)}
                                >
                                    {f.options.map(([value, label]) => (
                                        <MenuItem key={value || 'all'} value={value}>{label}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        ))}

                        <AnimatePresence initial={false}>
                            {filtersActive && (
                                <motion.div
                                    initial={{ opacity: 0, scale: 0.9 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    exit={{ opacity: 0, scale: 0.9 }}
                                    transition={{ duration: 0.2 }}
                                >
                                    <Button
                                        onClick={clearFilters}
                                        startIcon={<FilterOffIcon sx={{ fontSize: 17 }} />}
                                        sx={{
                                            textTransform: 'none', fontWeight: 600, borderRadius: '12px', px: 1.75,
                                            color: palette.rust, border: `1px solid ${rgba(palette.rust, 0.3)}`,
                                            background: rgba(palette.rust, 0.06),
                                            '&:hover': { background: rgba(palette.rust, 0.12) },
                                        }}
                                    >
                                        Clear
                                    </Button>
                                </motion.div>
                            )}
                        </AnimatePresence>

                        <Box
                            sx={{
                                ml: { xs: 0, xl: 'auto' }, px: 1.25, py: 0.6, borderRadius: '12px',
                                fontFamily: fontMono, fontSize: '0.66rem', fontWeight: 600, color: palette.inkSoft,
                                background: rgba(palette.ink, 0.04), border: `1px solid ${palette.hairlineSoft}`,
                            }}
                        >
                            {compact(filtered.length)} / {compact(companies.length)}
                        </Box>
                    </Box>
                </GlassPanel>

                {/* ── directory ────────────────────────────────────────────── */}
                <GlassPanel accent={palette.pine} delay={0.15} padding={{ xs: 1.75, md: 2.5 }}>
                    <PanelHead
                        icon={<BusinessIcon />}
                        title="Companies directory"
                        subtitle={`${compact(filtered.length)} ${filtered.length === 1 ? 'company' : 'companies'} matching your view`}
                        accent={palette.pine}
                    />

                    <TableContainer sx={{ mx: -0.5, px: 0.5 }}>
                        <Table sx={{ minWidth: 860 }}>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={headCellSx}>Company</TableCell>
                                    <TableCell sx={{ ...headCellSx, display: { xs: 'none', md: 'table-cell' } }}>Type</TableCell>
                                    <TableCell sx={headCellSx}>Status</TableCell>
                                    <TableCell sx={{ ...headCellSx, display: { xs: 'none', lg: 'table-cell' } }}>Payment</TableCell>
                                    <TableCell sx={{ ...headCellSx, display: { xs: 'none', lg: 'table-cell' } }}>Plan</TableCell>
                                    <TableCell sx={{ ...headCellSx, display: { xs: 'none', xl: 'table-cell' } }}>Joined</TableCell>
                                    <TableCell align="right" sx={headCellSx}>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loading ? (
                                    Array.from({ length: 6 }).map((_, i) => (
                                        <TableRow key={i}>
                                            <TableCell colSpan={7} sx={{ border: 0, py: 0.75 }}>
                                                <Skeleton variant="rounded" height={54} sx={{ borderRadius: '14px', bgcolor: palette.hairline }} />
                                            </TableCell>
                                        </TableRow>
                                    ))
                                ) : paginated.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={7} sx={{ border: 0 }}>
                                            {companies.length === 0 ? (
                                                <EmptyState
                                                    icon={<BusinessIcon />}
                                                    title="No companies yet"
                                                    text="Register the first exhibitor to start the directory."
                                                    action="Add your first company"
                                                    onAction={() => setFormState({ open: true, mode: 'add', company: null })}
                                                />
                                            ) : (
                                                <EmptyState icon={<SearchIcon />} title="Nothing matches" text="Try a different term or clear the filters." />
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    paginated.map((c, i) => {
                                        const accent = typeMeta(c.companyType).accent;
                                        return (
                                            <TableRow
                                                key={c.id}
                                                sx={{
                                                    opacity: 0,
                                                    animation: 'tfRowIn .45s cubic-bezier(.22,1,.36,1) forwards',
                                                    animationDelay: `${Math.min(i, 10) * 40}ms`,
                                                    transition: 'background .3s ease, box-shadow .3s ease',
                                                    '&:hover': {
                                                        background: `linear-gradient(90deg, ${rgba(accent, 0.07)}, transparent 70%)`,
                                                        boxShadow: `inset 3px 0 0 ${accent}`,
                                                    },
                                                    '&:hover .tf-actions': { opacity: 1 },
                                                }}
                                            >
                                                <TableCell sx={bodyCellSx}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
                                                        <CompanyAvatar company={c} size={38} radius={13} />
                                                        <Box sx={{ minWidth: 0 }}>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.7 }}>
                                                                <Typography sx={{ fontSize: '0.86rem', fontWeight: 600, color: palette.ink }} noWrap>
                                                                    {c.companyName || 'Unnamed company'}
                                                                </Typography>
                                                                {c.isVerified && <VerifiedIcon sx={{ fontSize: 14, color: palette.pine }} />}
                                                            </Box>
                                                            <Typography sx={{ fontSize: '0.72rem', color: palette.inkLight }} noWrap>
                                                                {c.headquarters || c.email || 'No location'}
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                </TableCell>
                                                <TableCell sx={{ ...bodyCellSx, display: { xs: 'none', md: 'table-cell' } }}>
                                                    <TypePill type={c.companyType} />
                                                </TableCell>
                                                <TableCell sx={bodyCellSx}><LifecyclePill company={c} /></TableCell>
                                                <TableCell sx={{ ...bodyCellSx, display: { xs: 'none', lg: 'table-cell' } }}>
                                                    <PaymentPill status={c.paymentStatus || 'not_required'} />
                                                </TableCell>
                                                <TableCell sx={{ ...bodyCellSx, display: { xs: 'none', lg: 'table-cell' } }}>
                                                    {c.subscription?.packageName ? (
                                                        <Tooltip title={priceLabel(c.subscription)} arrow>
                                                            <Box sx={{ display: 'inline-flex', flexDirection: 'column', gap: 0.4 }}>
                                                                <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: palette.ink }} noWrap>
                                                                    {c.subscription.packageName}
                                                                </Typography>
                                                                <SubscriptionPill status={c.subscription.status || 'inactive'} />
                                                            </Box>
                                                        </Tooltip>
                                                    ) : (
                                                        <MetaPill label="No plan" tone={palette.inkFaint} />
                                                    )}
                                                </TableCell>
                                                <TableCell sx={{ ...bodyCellSx, display: { xs: 'none', xl: 'table-cell' } }}>
                                                    <Typography sx={{ fontFamily: fontMono, fontSize: '0.72rem', color: palette.inkLight }}>
                                                        {formatDate(c.createdAt)}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell align="right" sx={bodyCellSx}>
                                                    <Box
                                                        className="tf-actions"
                                                        sx={{
                                                            display: 'flex', gap: 0.4, justifyContent: 'flex-end', flexWrap: 'wrap',
                                                            opacity: { xs: 1, md: 0.45 }, transition: 'opacity .3s ease',
                                                        }}
                                                    >
                                                        {[
                                                            {
                                                                key: 'open', title: 'Open company page', tone: palette.teal,
                                                                icon: <VisibilityIcon sx={{ fontSize: 17 }}  key="config-2386-0-3" />,
                                                                onClick: () => navigate(`/companies/${c.id}`),
                                                            },
                                                            {
                                                                key: 'details', title: 'Subscription & certificate', tone: palette.plum,
                                                                icon: <EmojiEventsIcon sx={{ fontSize: 17 }}  key="config-2391-1-3" />,
                                                                onClick: () => setDetails({ open: true, company: c }),
                                                            },
                                                            ...(!c.isVerified
                                                                ? [{
                                                                      key: 'verify', title: 'Verify company', tone: palette.pine,
                                                                      icon: <VerifiedIcon sx={{ fontSize: 17 }}  key="config-2397-0-3" />,
                                                                      onClick: () => verifyCompany(c),
                                                                  }]
                                                                : []),
                                                            {
                                                                key: 'edit', title: 'Edit', tone: palette.amber,
                                                                icon: <EditIcon sx={{ fontSize: 17 }}  key="config-2403-3-3" />,
                                                                onClick: () => setFormState({ open: true, mode: 'edit', company: c }),
                                                            },
                                                            {
                                                                key: 'block',
                                                                title: c.isBlocked ? 'Unblock' : 'Block',
                                                                tone: c.isBlocked ? palette.pine : palette.warning,
                                                                icon: c.isBlocked ? <CheckCircleIcon sx={{ fontSize: 17 }} /> : <BlockIcon sx={{ fontSize: 17 }} />,
                                                                onClick: () => setConfirm({ open: true, kind: 'block', company: c }),
                                                            },
                                                            {
                                                                key: 'delete', title: 'Delete', tone: palette.danger,
                                                                icon: <DeleteIcon sx={{ fontSize: 17 }}  key="config-2415-5-3" />,
                                                                onClick: () => setConfirm({ open: true, kind: 'delete', company: c }),
                                                            },
                                                        ].map((action) => (
                                                            <Tooltip key={action.key} title={action.title} arrow>
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={action.onClick}
                                                                    sx={{
                                                                        width: 30, height: 30, borderRadius: '10px', color: action.tone,
                                                                        border: `1px solid ${rgba(action.tone, 0.18)}`,
                                                                        background: rgba(action.tone, 0.06),
                                                                        transition: 'transform .25s ease, background .25s ease',
                                                                        '&:hover': { background: rgba(action.tone, 0.16), transform: 'translateY(-2px)' },
                                                                    }}
                                                                >
                                                                    {action.icon}
                                                                </IconButton>
                                                            </Tooltip>
                                                        ))}
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <TablePagination
                        component="div"
                        rowsPerPageOptions={[5, 10, 25, 50]}
                        count={filtered.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={(_, next) => setPage(next)}
                        onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                        sx={{
                            mt: 1, borderTop: `1px solid ${palette.hairlineSoft}`,
                            '& .MuiTablePagination-toolbar': { flexWrap: 'wrap', px: 0 },
                            '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                                fontFamily: fontMono, fontSize: '0.72rem', color: palette.inkLight,
                            },
                        }}
                    />
                </GlassPanel>
            </Container>

            {/* ── dialogs ──────────────────────────────────────────────────── */}
            <CompanyFormDialog
                open={formState.open}
                mode={formState.mode}
                company={formState.company}
                authorUid={authorUid}
                onClose={() => setFormState((s) => ({ ...s, open: false }))}
                onSaved={() => loadCompanies(true)}
            />

            <CompanyDetailsDialog
                open={details.open}
                company={details.company}
                onClose={() => setDetails((s) => ({ ...s, open: false }))}
                onIssueCertificate={(company) => setCertificate({ open: true, company })}
            />

            <CertificateDialog
                open={certificate.open}
                company={certificate.company}
                onClose={() => setCertificate((s) => ({ ...s, open: false }))}
                onIssued={(registrationNumber) => persistCertificate(certificate.company, registrationNumber)}
            />

            <ConfirmDialog
                open={confirm.open && confirm.kind === 'delete'}
                busy={busy}
                tone={palette.danger}
                icon={<DeleteIcon />}
                title="Delete this company?"
                body={`${confirm.company?.companyName || 'This company'} will be removed from the directory. Meetings and users referencing it will show as unknown.`}
                confirmLabel="Delete"
                onClose={() => setConfirm({ open: false, kind: null, company: null })}
                onConfirm={confirmDelete}
            />

            <ConfirmDialog
                open={confirm.open && confirm.kind === 'block'}
                busy={busy}
                tone={confirm.company?.isBlocked ? palette.pine : palette.warning}
                icon={confirm.company?.isBlocked ? <CheckCircleIcon /> : <BlockIcon />}
                title={confirm.company?.isBlocked ? 'Unblock this company?' : 'Block this company?'}
                body={
                    confirm.company?.isBlocked
                        ? `${confirm.company?.companyName || 'They'} will appear in the directory again.`
                        : `${confirm.company?.companyName || 'They'} will be hidden from attendees and unable to take meetings.`
                }
                confirmLabel={confirm.company?.isBlocked ? 'Unblock' : 'Block'}
                onClose={() => setConfirm({ open: false, kind: null, company: null })}
                onConfirm={confirmBlock}
            />

            <style>{`
                @keyframes tfPulse {
                    0%, 100% { opacity: 1; transform: scale(1); }
                    50%      { opacity: .45; transform: scale(.82); }
                }
                @keyframes tfRule {
                    0%, 100% { opacity: .7; transform: scaleX(1); }
                    50%      { opacity: 1;  transform: scaleX(1.35); }
                }
                @keyframes tfSpin { to { transform: rotate(360deg); } }
                @keyframes tfRowIn {
                    from { opacity: 0; transform: translateY(10px); }
                    to   { opacity: 1; transform: translateY(0); }
                }
                @keyframes tfGrain {
                    0%   { transform: translate(0, 0); }
                    25%  { transform: translate(-2%, 1%); }
                    50%  { transform: translate(1%, -2%); }
                    75%  { transform: translate(-1%, -1%); }
                    100% { transform: translate(0, 0); }
                }
                * { scrollbar-width: thin; scrollbar-color: ${rgba(palette.ink, 0.16)} transparent; }
                *::-webkit-scrollbar { width: 6px; height: 6px; }
                *::-webkit-scrollbar-track { background: transparent; }
                *::-webkit-scrollbar-thumb { background: ${rgba(palette.ink, 0.14)}; border-radius: 999px; }
                *::-webkit-scrollbar-thumb:hover { background: ${rgba(palette.ink, 0.26)}; }
                @media (prefers-reduced-motion: reduce) {
                    *, *::before, *::after {
                        animation-duration: .001ms !important;
                        animation-iteration-count: 1 !important;
                        transition-duration: .001ms !important;
                    }
                }
            `}</style>
        </Box>
    );
};

export default Companies;